// types.ts
var STD_MODULES = [
  "core",
  "math",
  "str",
  "array",
  "object",
  "json",
  "time",
  "validate",
  "format",
  "async",
  "nn",
  "tensor",
  "train",
  "prob",
  "os",
  "browser",
  "graph",
  "contract",
  "data",
  "composition",
  "vec",
  "geo",
  "grid",
  "anim",
  "ease",
  "noise",
  "path",
  // Substrate modules (Almadar_Rabit_LOLO_Plan.md Phase 3)
  "llm",
  "workspace",
  "session",
  "memory",
  "trace",
  "behavior",
  "integration"
];
function getModuleFromOperator(operator) {
  const parts = operator.split("/");
  if (parts.length !== 2) return null;
  const module = parts[0];
  return STD_MODULES.includes(module) ? module : null;
}
function isStdOperator(operator) {
  return getModuleFromOperator(operator) !== null;
}

// modules/core.ts
var NUMBER = "number";
var BOOLEAN = "boolean";
var STRING = "string";
var ANY = "any";
var SEXPR = { kind: "sexpr" };
var BINDING = { kind: "binding" };
var ENTITY_REF = { kind: "entityRef" };
var EVENT_KEY = { kind: "eventKey" };
var UI_SLOT = { kind: "uiSlot" };
var PATTERN_TYPE = { kind: "patternType" };
var PERSIST_ACTION = {
  kind: "union",
  of: [
    { kind: "literal", value: "create" },
    { kind: "literal", value: "update" },
    { kind: "literal", value: "delete" },
    { kind: "literal", value: "clear" },
    { kind: "literal", value: "batch" }
  ]
};
var SET_OPERATION = {
  kind: "union",
  of: [
    { kind: "literal", value: "increment" },
    { kind: "literal", value: "decrement" },
    { kind: "literal", value: "multiply" },
    { kind: "literal", value: "append" },
    { kind: "literal", value: "remove" }
  ]
};
var NOTIFY_CHANNEL = {
  kind: "union",
  of: [
    { kind: "literal", value: "email" },
    { kind: "literal", value: "push" },
    { kind: "literal", value: "sms" },
    { kind: "literal", value: "in-app" }
  ]
};
var CORE_OPERATORS = {
  // --- arithmetic primitives --------------------------------------------------
  "+": {
    module: "core",
    category: "arithmetic",
    minArity: 2,
    maxArity: null,
    description: "Add numbers",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "...nums", type: NUMBER, description: "Numbers to sum" }],
    example: '["+", 1, 2, 3] // => 6'
  },
  "-": {
    module: "core",
    category: "arithmetic",
    minArity: 1,
    maxArity: 2,
    description: "Subtract or negate",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "a", type: NUMBER, description: "Left operand or value to negate" },
      { name: "b", type: NUMBER, description: "Right operand", optional: true }
    ],
    example: '["-", 5, 2] // => 3; ["-", 5] // => -5'
  },
  "*": {
    module: "core",
    category: "arithmetic",
    minArity: 2,
    maxArity: null,
    description: "Multiply numbers",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "...nums", type: NUMBER, description: "Numbers to multiply" }],
    example: '["*", 2, 3, 4] // => 24'
  },
  "/": {
    module: "core",
    category: "arithmetic",
    minArity: 2,
    maxArity: 2,
    description: "Divide numbers",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "a", type: NUMBER, description: "Numerator" },
      { name: "b", type: NUMBER, description: "Denominator" }
    ],
    example: '["/", 10, 2] // => 5'
  },
  "%": {
    module: "core",
    category: "arithmetic",
    minArity: 2,
    maxArity: 2,
    description: "Modulo (remainder)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "a", type: NUMBER, description: "Dividend" },
      { name: "b", type: NUMBER, description: "Divisor" }
    ],
    example: '["%", 10, 3] // => 1'
  },
  // --- comparison -------------------------------------------------------------
  "=": {
    module: "core",
    category: "comparison",
    minArity: 2,
    maxArity: 2,
    description: "Equal to",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "a", type: ANY, description: "Left operand" },
      { name: "b", type: ANY, description: "Right operand" }
    ],
    example: '["=", "active", "active"]'
  },
  "==": {
    module: "core",
    category: "comparison",
    minArity: 2,
    maxArity: 2,
    description: "Equal to (canonical .lolo spelling)",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "a", type: ANY, description: "Left operand" },
      { name: "b", type: ANY, description: "Right operand" }
    ],
    example: '["==", "active", "active"]'
  },
  "!=": {
    module: "core",
    category: "comparison",
    minArity: 2,
    maxArity: 2,
    description: "Not equal to",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "a", type: ANY, description: "Left operand" },
      { name: "b", type: ANY, description: "Right operand" }
    ],
    example: '["!=", 1, null]'
  },
  "<": {
    module: "core",
    category: "comparison",
    minArity: 2,
    maxArity: 2,
    description: "Less than",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "a", type: NUMBER, description: "Left operand" },
      { name: "b", type: NUMBER, description: "Right operand" }
    ],
    example: '["<", 5, 10]'
  },
  ">": {
    module: "core",
    category: "comparison",
    minArity: 2,
    maxArity: 2,
    description: "Greater than",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "a", type: NUMBER, description: "Left operand" },
      { name: "b", type: NUMBER, description: "Right operand" }
    ],
    example: '[">", 10, 0]'
  },
  "<=": {
    module: "core",
    category: "comparison",
    minArity: 2,
    maxArity: 2,
    description: "Less than or equal",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "a", type: NUMBER, description: "Left operand" },
      { name: "b", type: NUMBER, description: "Right operand" }
    ],
    example: '["<=", 100, 100]'
  },
  ">=": {
    module: "core",
    category: "comparison",
    minArity: 2,
    maxArity: 2,
    description: "Greater than or equal",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "a", type: NUMBER, description: "Left operand" },
      { name: "b", type: NUMBER, description: "Right operand" }
    ],
    example: '[">=", 18, 18]'
  },
  // --- logic ------------------------------------------------------------------
  and: {
    module: "core",
    category: "logic",
    minArity: 2,
    maxArity: null,
    description: "Logical AND (returns the last truthy value, NOT a boolean \u2014 short-circuit)",
    hasSideEffects: false,
    returnType: "boolean",
    returnSemantics: "last-truthy-of-args",
    params: [{ name: "...conds", type: BOOLEAN, description: "Boolean expressions to AND" }],
    example: '["and", ["=", "active", "active"], [">", 5, 0]]'
  },
  or: {
    module: "core",
    category: "logic",
    minArity: 2,
    maxArity: null,
    description: "Logical OR (returns the first truthy value, NOT a boolean \u2014 short-circuit)",
    hasSideEffects: false,
    returnType: "boolean",
    returnSemantics: "first-truthy-of-args",
    params: [{ name: "...conds", type: BOOLEAN, description: "Boolean expressions to OR" }],
    example: '["or", ["=", "admin", "user"], ["=", "admin", "owner"]]'
  },
  not: {
    module: "core",
    category: "logic",
    minArity: 1,
    maxArity: 1,
    description: "Logical NOT",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "cond", type: BOOLEAN, description: "Boolean to negate" }],
    example: '["not", false]'
  },
  if: {
    module: "core",
    category: "logic",
    minArity: 3,
    maxArity: 3,
    description: "Conditional expression (ternary). Return type is union of branch types.",
    hasSideEffects: false,
    returnType: "any",
    returnSemantics: "branch-union",
    params: [
      { name: "cond", type: BOOLEAN, description: "Condition to evaluate" },
      { name: "then", type: ANY, description: "Value or effect if true" },
      { name: "else", type: ANY, description: "Value or effect if false" }
    ],
    example: '["if", [">", 5, 0], "alive", "dead"]'
  },
  // --- control ----------------------------------------------------------------
  let: {
    module: "core",
    category: "control",
    minArity: 2,
    maxArity: 2,
    description: "Bind local variables for a body expression",
    hasSideEffects: false,
    returnType: "any",
    params: [
      {
        name: "bindings",
        type: { kind: "array", of: { kind: "array", of: ANY } },
        description: "Array of [name, value] pairs"
      },
      { name: "body", type: SEXPR, description: "Expression or effect list using the bindings" }
    ],
    example: '["let", [["x", 10]], ["+", "x", 1]]'
  },
  do: {
    module: "core",
    category: "control",
    minArity: 1,
    maxArity: null,
    description: "Sequential execution of multiple effects/expressions",
    hasSideEffects: false,
    returnType: "any",
    params: [{ name: "...exprs", type: SEXPR, description: "Effects/expressions to run in order" }],
    example: '["do", ["set", "@entity.x", 0], ["set", "@entity.y", 0]]'
  },
  list: {
    module: "core",
    category: "control",
    minArity: 0,
    maxArity: null,
    description: "Literal array constructor \u2014 evaluates each argument and returns them as an array. Lowering wraps a literal list bound for a literal-array slot in `list` so it is unambiguously data, never a call (disambiguates a list whose first element collides with an operator name, e.g. [path method \u2026]).",
    hasSideEffects: false,
    returnType: "array",
    params: [{ name: "...items", type: SEXPR, description: "Items to collect into a literal array" }],
    example: '["list", "path", "method", "status"] // => ["path", "method", "status"]'
  },
  when: {
    module: "core",
    category: "control",
    minArity: 2,
    maxArity: 2,
    description: "Conditional effect without an else branch",
    hasSideEffects: false,
    returnType: "void",
    params: [
      { name: "cond", type: BOOLEAN, description: "Guard expression" },
      { name: "effect", type: SEXPR, description: "Effect to run if cond is true" }
    ],
    example: '["when", [">", "@entity.health", 0], ["emit", "ALIVE"]]'
  },
  fn: {
    module: "core",
    category: "control",
    minArity: 2,
    maxArity: 2,
    description: "Lambda expression (used for per-item renders and transforms)",
    hasSideEffects: false,
    returnType: "function",
    acceptsLambda: true,
    lambdaArgPosition: 1,
    params: [
      { name: "paramName", type: STRING, description: "Name of the parameter binding" },
      { name: "body", type: SEXPR, description: "Expression evaluated per invocation" }
    ],
    example: '["fn", "item", { "type": "typography", "content": "@item.title" }]'
  },
  // --- effect -----------------------------------------------------------------
  set: {
    module: "core",
    category: "effect",
    minArity: 2,
    maxArity: 2,
    description: "Set a binding to a value",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "binding", type: BINDING, description: 'Target binding (e.g. "@entity.field")' },
      { name: "value", type: ANY, description: "Value to assign (literal or expression)" }
    ],
    example: '["set", "@entity.health", 100]',
    effect: {
      kind: "set",
      // success emit carries the written value; the evaluator coerces to the
      // binding's declared type, so 'any' is the general shape. A future pass
      // can narrow per-binding via resolveBinding.
      produces: ANY,
      config: { operation: SET_OPERATION }
    }
  },
  emit: {
    module: "core",
    category: "effect",
    minArity: 1,
    maxArity: 2,
    description: "Emit an event onto the bus",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "event", type: EVENT_KEY, description: "Event key" },
      { name: "payload", type: ENTITY_REF, description: "Optional payload", optional: true }
    ],
    example: '["emit", "PLAYER_DIED", { "playerId": "p1" }]',
    effect: {
      kind: "emit",
      produces: {
        kind: "object",
        fields: { event: EVENT_KEY, payload: ANY },
        open: true
      }
    }
  },
  persist: {
    module: "core",
    category: "effect",
    minArity: 2,
    maxArity: 4,
    description: "Create, update, delete, clear, or batch entity records. Optional trailing { emit: { success, failure } } options object attaches closed-circuit emit routing.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "action", type: PERSIST_ACTION, description: "Persist action" },
      { name: "entity", type: { kind: "entity" }, description: "Target entity name" },
      { name: "data", type: ENTITY_REF, description: "Payload (create/update) or entity id (delete)", optional: true },
      {
        name: "options",
        type: {
          kind: "object",
          fields: {
            emit: { kind: "object", fields: {}, open: true }
          },
          open: true
        },
        description: "Optional trailing options object carrying { emit: { success, failure } } closed-circuit routing",
        optional: true
      }
    ],
    example: '["persist", "create", "Task", { "title": "Buy milk" }, { "emit": { "success": "TASK_CREATED" } }]',
    effect: {
      kind: "persist",
      // Action-discriminated union. The compiler narrows at the call site
      // based on the action arg literal to pick the right branch when
      // checking the emit.success event's declared payload.
      //   create/update -> the resulting entity record
      //   delete/clear  -> { id, deleted }
      //   batch         -> summary of operations
      produces: {
        kind: "union",
        of: [
          { kind: "entity" },
          {
            kind: "object",
            fields: { id: STRING, deleted: BOOLEAN },
            open: false
          },
          {
            kind: "object",
            fields: {
              operations: { kind: "array", of: ANY },
              completedCount: NUMBER,
              totalCount: NUMBER
            },
            open: false
          }
        ]
      }
    }
  },
  navigate: {
    module: "core",
    category: "effect",
    minArity: 1,
    maxArity: 3,
    description: "Navigate to a route: (navigate path payload? options?) \u2014 options carries { crumb } labelling the navigation-stack entry for the target page",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "path", type: STRING, description: "Route path (supports :param placeholders)" },
      {
        name: "params",
        type: { kind: "object", fields: {}, open: true },
        description: "Optional route params",
        optional: true
      },
      {
        name: "options",
        type: { kind: "object", fields: {}, open: true },
        description: "Optional navigation options: { crumb } labels the navigation-stack entry for the target page",
        optional: true
      }
    ],
    example: '["navigate", "/tasks/:id", { "id": "t1" }]',
    effect: { kind: "navigate" }
  },
  "navigate-back": {
    module: "core",
    category: "effect",
    minArity: 0,
    maxArity: 0,
    description: "Pop the orbital-scoped navigation stack and return to the previous entry",
    hasSideEffects: true,
    returnType: "void",
    effect: { kind: "navigate-back" }
  },
  notify: {
    module: "core",
    category: "effect",
    minArity: 1,
    maxArity: 2,
    description: "Show a notification (in-app, email, push, sms)",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "channel", type: NOTIFY_CHANNEL, description: "Delivery channel" },
      { name: "message", type: STRING, description: "Message body", optional: true }
    ],
    example: '["notify", "in-app", "Task created successfully"]',
    effect: { kind: "notify" }
  },
  log: {
    module: "core",
    category: "effect",
    minArity: 1,
    maxArity: 1,
    description: "Emit a structured info log line (compiled + runtime)",
    hasSideEffects: true,
    returnType: "void",
    params: [{ name: "message", type: STRING, description: "Log message" }],
    example: '["log", "Context below compact threshold"]',
    effect: { kind: "log" }
  },
  spawn: {
    module: "core",
    category: "effect",
    minArity: 1,
    maxArity: 2,
    description: "Spawn a new entity instance (games)",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "entity", type: { kind: "entity" }, description: "Entity name to spawn" },
      {
        name: "initialState",
        type: { kind: "object", fields: {}, open: true },
        description: "Initial field values",
        optional: true
      }
    ],
    example: '["spawn", "Bullet", { "x": 10, "y": 20 }]',
    effect: { kind: "spawn" }
  },
  despawn: {
    module: "core",
    category: "effect",
    minArity: 0,
    maxArity: 1,
    description: "Despawn an entity instance (games)",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "entityId", type: STRING, description: "Target entity id (defaults to @entity.id)", optional: true }
    ],
    example: '["despawn", "@entity.id"]',
    effect: { kind: "despawn" }
  },
  fetch: {
    module: "core",
    category: "effect",
    minArity: 1,
    maxArity: 2,
    description: "Fetch an entity (by id) or a collection (by filter) from persistence",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "entity", type: { kind: "entity" }, description: "Target entity name" },
      {
        name: "options",
        type: {
          kind: "object",
          fields: {
            id: STRING,
            filter: ANY,
            limit: NUMBER,
            offset: NUMBER,
            include: { kind: "array", of: STRING },
            emit: { kind: "object", fields: {}, open: true }
          },
          open: true
        },
        description: "Fetch options: id | filter | limit | offset | include | emit",
        optional: true
      }
    ],
    example: '["fetch", "Task", { "id": "t1", "emit": { "success": "TASK_LOADED" } }]',
    effect: {
      kind: "fetch",
      produces: {
        kind: "object",
        fields: {
          data: {
            kind: "union",
            of: [
              { kind: "entity" },
              { kind: "array", of: { kind: "entity" } }
            ]
          },
          totalCount: "number"
        },
        open: false
      }
    }
  },
  "fetch-stream": {
    module: "core",
    category: "effect",
    minArity: 1,
    maxArity: 2,
    description: "Stream an LLM or HTTP response, emitting on_message per chunk, success on completion, failure on error",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "entity", type: { kind: "entity" }, description: "Target entity name" },
      {
        name: "options",
        type: {
          kind: "object",
          fields: {
            id: STRING,
            filter: ANY,
            emit: { kind: "object", fields: {}, open: true }
          },
          open: true
        },
        description: "Stream options: id | filter | emit",
        optional: true
      }
    ],
    example: '["fetch-stream", "Message", { "emit": { "on_message": "CHUNK_RECEIVED", "success": "STREAM_DONE", "failure": "STREAM_FAILED" } }]',
    effect: {
      kind: "fetch-stream",
      produces: { kind: "entity" }
    }
  },
  "send-server": {
    module: "core",
    category: "effect",
    minArity: 1,
    maxArity: 2,
    description: "Send an orbital event to the server over WebSocket (client-side; chat/real-time trigger)",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "event", type: EVENT_KEY, description: "Target orbital event name on the server" },
      {
        name: "payload",
        type: { kind: "object", fields: {}, open: true },
        description: "Event payload to forward to the server",
        optional: true
      }
    ],
    example: '["send-server", "CHAT_MESSAGE", { "text": "hello" }]',
    effect: { kind: "send-server" }
  },
  ref: {
    module: "core",
    category: "effect",
    minArity: 1,
    maxArity: 2,
    description: "Reactive entity reference (deprecated, use fetch with listens in V2)",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "entity", type: { kind: "entity" }, description: "Target entity name" },
      {
        name: "options",
        type: {
          kind: "object",
          fields: {
            id: STRING,
            filter: ANY,
            include: { kind: "array", of: STRING },
            emit: { kind: "object", fields: {}, open: true }
          },
          open: true
        },
        description: "Ref options: id | filter | include | emit",
        optional: true
      }
    ],
    example: '["ref", "Task"]',
    effect: {
      kind: "ref",
      // Same wrapper fetch emits. Kept for the V2 transition period;
      // scheduled for deprecation in a later phase (see Almadar_Entity_V2_Plan.md).
      produces: {
        kind: "object",
        fields: {
          data: {
            kind: "union",
            of: [
              { kind: "entity" },
              { kind: "array", of: { kind: "entity" } }
            ]
          },
          totalCount: "number"
        },
        open: false
      }
    }
  },
  "call-service": {
    module: "core",
    category: "effect",
    minArity: 2,
    maxArity: 4,
    description: "Invoke an external service action. Optional trailing { emit: { success, failure } } options object attaches closed-circuit emit routing (V2 frame contract).",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "service", type: STRING, description: 'Service name (e.g. "llm", "stripe")' },
      { name: "action", type: STRING, description: 'Service action (e.g. "generate", "charge")' },
      {
        name: "params",
        type: { kind: "object", fields: {}, open: true },
        description: "Service-specific params",
        optional: true
      },
      {
        name: "options",
        type: { kind: "object", fields: {}, open: true },
        description: 'Optional trailing options object. Set `{ emit: { success: "...", failure: "..." } }` to wire closed-circuit result routing (V2 frame).',
        optional: true
      }
    ],
    example: '["call-service", "llm", "generate", { "userPrompt": "Hello" }, { "emit": { "success": "LlmCompleted", "failure": "LlmFailed" } }]',
    effect: {
      kind: "call-service",
      // TODO(entity-v2): narrow to the declared service return type once
      // per-service return shapes live on the orbital services block.
      // Today the emit.success payload is whatever the service adapter returns.
      produces: ANY
    }
  },
  "render-ui": {
    module: "core",
    category: "effect",
    minArity: 2,
    maxArity: 3,
    description: "Render a pattern into a UI slot",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "slot", type: UI_SLOT, description: "Target UI slot (main, sidebar, modal, hud, ...)" },
      {
        name: "pattern",
        type: {
          kind: "union",
          of: [
            { kind: "object", fields: { patternType: PATTERN_TYPE }, open: true },
            { kind: "literal", value: null }
          ]
        },
        description: "Pattern config (or null to clear the slot)"
      },
      {
        name: "props",
        type: { kind: "object", fields: {}, open: true },
        description: "Extra props forwarded to the pattern",
        optional: true
      }
    ],
    example: '["render-ui", "main", { "patternType": "entity-table", "columns": ["name"] }]',
    effect: { kind: "render-ui" }
  }
};

// modules/math.ts
var MATH_OPERATORS = {
  "math/abs": {
    module: "math",
    category: "std-math",
    minArity: 1,
    maxArity: 1,
    description: "Absolute value",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "n", type: "number", description: "The number" }],
    example: '["math/abs", -5] // => 5'
  },
  "math/min": {
    module: "math",
    category: "std-math",
    minArity: 2,
    maxArity: null,
    description: "Minimum of values",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "...nums", type: "number[]", description: "Numbers to compare" }],
    example: '["math/min", 3, 1, 4] // => 1'
  },
  "math/max": {
    module: "math",
    category: "std-math",
    minArity: 2,
    maxArity: null,
    description: "Maximum of values",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "...nums", type: "number[]", description: "Numbers to compare" }],
    example: '["math/max", 3, 1, 4] // => 4'
  },
  "math/clamp": {
    module: "math",
    category: "std-math",
    minArity: 3,
    maxArity: 3,
    description: "Constrain value to range [min, max]",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "n", type: "number", description: "The value to clamp" },
      { name: "min", type: "number", description: "Minimum bound" },
      { name: "max", type: "number", description: "Maximum bound" }
    ],
    example: '["math/clamp", 150, 0, 100] // => 100'
  },
  "math/floor": {
    module: "math",
    category: "std-math",
    minArity: 1,
    maxArity: 1,
    description: "Round down to integer",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "n", type: "number", description: "The number" }],
    example: '["math/floor", 3.7] // => 3'
  },
  "math/ceil": {
    module: "math",
    category: "std-math",
    minArity: 1,
    maxArity: 1,
    description: "Round up to integer",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "n", type: "number", description: "The number" }],
    example: '["math/ceil", 3.2] // => 4'
  },
  "math/round": {
    module: "math",
    category: "std-math",
    minArity: 1,
    maxArity: 2,
    description: "Round to nearest integer or specified decimals",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "n", type: "number", description: "The number" },
      { name: "decimals", type: "number", description: "Decimal places", optional: true, defaultValue: 0 }
    ],
    example: '["math/round", 3.456, 2] // => 3.46'
  },
  "math/pow": {
    module: "math",
    category: "std-math",
    minArity: 2,
    maxArity: 2,
    description: "Exponentiation (base^exp)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "base", type: "number", description: "The base" },
      { name: "exp", type: "number", description: "The exponent" }
    ],
    example: '["math/pow", 2, 8] // => 256'
  },
  "math/sqrt": {
    module: "math",
    category: "std-math",
    minArity: 1,
    maxArity: 1,
    description: "Square root",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "n", type: "number", description: "The number" }],
    example: '["math/sqrt", 16] // => 4'
  },
  "math/mod": {
    module: "math",
    category: "std-math",
    minArity: 2,
    maxArity: 2,
    description: "Modulo (remainder)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "a", type: "number", description: "Dividend" },
      { name: "b", type: "number", description: "Divisor" }
    ],
    example: '["math/mod", 7, 3] // => 1'
  },
  "math/sign": {
    module: "math",
    category: "std-math",
    minArity: 1,
    maxArity: 1,
    description: "Returns -1, 0, or 1 indicating sign",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "n", type: "number", description: "The number" }],
    example: '["math/sign", -42] // => -1'
  },
  "math/lerp": {
    module: "math",
    category: "std-math",
    minArity: 3,
    maxArity: 3,
    description: "Linear interpolation between a and b by factor t",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "a", type: "number", description: "Start value" },
      { name: "b", type: "number", description: "End value" },
      { name: "t", type: "number", description: "Interpolation factor (0-1)" }
    ],
    example: '["math/lerp", 0, 100, 0.5] // => 50'
  },
  "math/map": {
    module: "math",
    category: "std-math",
    minArity: 5,
    maxArity: 5,
    description: "Map value from one range to another",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "n", type: "number", description: "The value" },
      { name: "inMin", type: "number", description: "Input range minimum" },
      { name: "inMax", type: "number", description: "Input range maximum" },
      { name: "outMin", type: "number", description: "Output range minimum" },
      { name: "outMax", type: "number", description: "Output range maximum" }
    ],
    example: '["math/map", 5, 0, 10, 0, 100] // => 50'
  },
  "math/random": {
    module: "math",
    category: "std-math",
    minArity: 0,
    maxArity: 0,
    description: "Random number between 0 (inclusive) and 1 (exclusive)",
    hasSideEffects: false,
    returnType: "number",
    params: [],
    example: '["math/random"] // => 0.7234...'
  },
  "math/randomInt": {
    module: "math",
    category: "std-math",
    minArity: 2,
    maxArity: 2,
    description: "Random integer in range [min, max] (inclusive)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "min", type: "number", description: "Minimum (inclusive)" },
      { name: "max", type: "number", description: "Maximum (inclusive)" }
    ],
    example: '["math/randomInt", 1, 6] // => 4'
  },
  "math/default": {
    module: "math",
    category: "std-math",
    minArity: 2,
    maxArity: 2,
    description: "Return default if value is null, undefined, or NaN",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "n", type: "number | null", description: "The value" },
      { name: "default", type: "number", description: "Default value" }
    ],
    example: '["math/default", null, 0] // => 0'
  },
  "math/sin": {
    module: "math",
    category: "std-math",
    minArity: 1,
    maxArity: 1,
    description: "Sine of an angle in radians",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "rad", type: "number", description: "Angle in radians" }],
    example: '["math/sin", 0] // => 0'
  },
  "math/cos": {
    module: "math",
    category: "std-math",
    minArity: 1,
    maxArity: 1,
    description: "Cosine of an angle in radians",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "rad", type: "number", description: "Angle in radians" }],
    example: '["math/cos", 0] // => 1'
  },
  "math/tan": {
    module: "math",
    category: "std-math",
    minArity: 1,
    maxArity: 1,
    description: "Tangent of an angle in radians",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "rad", type: "number", description: "Angle in radians" }],
    example: '["math/tan", 0] // => 0'
  },
  "math/atan2": {
    module: "math",
    category: "std-math",
    minArity: 2,
    maxArity: 2,
    description: "Angle in radians from the positive x-axis to point (x, y)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "y", type: "number", description: "Y component" },
      { name: "x", type: "number", description: "X component" }
    ],
    example: '["math/atan2", 1, 0] // => 1.5707963267948966'
  },
  "math/asin": {
    module: "math",
    category: "std-math",
    minArity: 1,
    maxArity: 1,
    description: "Arcsine (inverse sine) in radians",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "x", type: "number", description: "Value in [-1, 1]" }],
    example: '["math/asin", 1] // => 1.5707963267948966'
  },
  "math/acos": {
    module: "math",
    category: "std-math",
    minArity: 1,
    maxArity: 1,
    description: "Arccosine (inverse cosine) in radians",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "x", type: "number", description: "Value in [-1, 1]" }],
    example: '["math/acos", 1] // => 0'
  },
  "math/atan": {
    module: "math",
    category: "std-math",
    minArity: 1,
    maxArity: 1,
    description: "Arctangent (inverse tangent) in radians",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "x", type: "number", description: "Value" }],
    example: '["math/atan", 1] // => 0.7853981633974483'
  },
  "math/hypot": {
    module: "math",
    category: "std-math",
    minArity: 2,
    maxArity: 2,
    description: "Euclidean length sqrt(a\xB2+b\xB2)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "a", type: "number", description: "First leg" },
      { name: "b", type: "number", description: "Second leg" }
    ],
    example: '["math/hypot", 3, 4] // => 5'
  },
  "math/deg-rad": {
    module: "math",
    category: "std-math",
    minArity: 1,
    maxArity: 1,
    description: "Convert degrees to radians",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "deg", type: "number", description: "Angle in degrees" }],
    example: '["math/deg-rad", 180] // => 3.141592653589793'
  },
  "math/rad-deg": {
    module: "math",
    category: "std-math",
    minArity: 1,
    maxArity: 1,
    description: "Convert radians to degrees",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "rad", type: "number", description: "Angle in radians" }],
    example: '["math/rad-deg", 3.141592653589793] // => 180'
  },
  "math/wrap": {
    module: "math",
    category: "std-math",
    minArity: 3,
    maxArity: 3,
    description: "Wrap a value into the half-open range [min, max)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "v", type: "number", description: "The value" },
      { name: "min", type: "number", description: "Range minimum (inclusive)" },
      { name: "max", type: "number", description: "Range maximum (exclusive)" }
    ],
    example: '["math/wrap", 7, 0, 5] // => 2'
  },
  "math/approach": {
    module: "math",
    category: "std-math",
    minArity: 3,
    maxArity: 3,
    description: "Move cur toward target by at most maxDelta",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "cur", type: "number", description: "Current value" },
      { name: "target", type: "number", description: "Target value" },
      { name: "maxDelta", type: "number", description: "Maximum step magnitude" }
    ],
    example: '["math/approach", 0, 10, 3] // => 3'
  }
};

// modules/str.ts
var STR_OPERATORS = {
  "str/len": {
    module: "str",
    category: "std-str",
    minArity: 1,
    maxArity: 1,
    description: "String length",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "s", type: "string", description: "The string" }],
    example: '["str/len", "hello"] // => 5'
  },
  "str/concat": {
    module: "str",
    category: "std-str",
    minArity: 1,
    maxArity: 99,
    description: "Concatenate strings together",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "strings", type: "string", description: "Strings to concatenate" }],
    example: '["str/concat", "/users/", "123"] // => "/users/123"'
  },
  "str/upper": {
    module: "str",
    category: "std-str",
    minArity: 1,
    maxArity: 1,
    description: "Convert to uppercase",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "s", type: "string", description: "The string" }],
    example: '["str/upper", "hello"] // => "HELLO"'
  },
  "str/lower": {
    module: "str",
    category: "std-str",
    minArity: 1,
    maxArity: 1,
    description: "Convert to lowercase",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "s", type: "string", description: "The string" }],
    example: '["str/lower", "HELLO"] // => "hello"'
  },
  "str/trim": {
    module: "str",
    category: "std-str",
    minArity: 1,
    maxArity: 1,
    description: "Remove leading and trailing whitespace",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "s", type: "string", description: "The string" }],
    example: '["str/trim", "  hello  "] // => "hello"'
  },
  "str/trimStart": {
    module: "str",
    category: "std-str",
    minArity: 1,
    maxArity: 1,
    description: "Remove leading whitespace",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "s", type: "string", description: "The string" }],
    example: '["str/trimStart", "  hello"] // => "hello"'
  },
  "str/trimEnd": {
    module: "str",
    category: "std-str",
    minArity: 1,
    maxArity: 1,
    description: "Remove trailing whitespace",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "s", type: "string", description: "The string" }],
    example: '["str/trimEnd", "hello  "] // => "hello"'
  },
  "str/split": {
    module: "str",
    category: "std-str",
    minArity: 2,
    maxArity: 2,
    description: "Split string into array by delimiter",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "s", type: "string", description: "The string" },
      { name: "delim", type: "string", description: "Delimiter" }
    ],
    example: '["str/split", "a,b,c", ","] // => ["a", "b", "c"]'
  },
  "str/join": {
    module: "str",
    category: "std-str",
    minArity: 2,
    maxArity: 2,
    description: "Join array elements into string",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "arr", type: "array", description: "Array to join" },
      { name: "delim", type: "string", description: "Delimiter" }
    ],
    example: '["str/join", ["a", "b", "c"], ", "] // => "a, b, c"'
  },
  "str/slice": {
    module: "str",
    category: "std-str",
    minArity: 2,
    maxArity: 3,
    description: "Extract substring",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "s", type: "string", description: "The string" },
      { name: "start", type: "number", description: "Start index" },
      { name: "end", type: "number", description: "End index (exclusive)", optional: true }
    ],
    example: '["str/slice", "hello", 1, 4] // => "ell"'
  },
  "str/replace": {
    module: "str",
    category: "std-str",
    minArity: 3,
    maxArity: 3,
    description: "Replace first occurrence",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "s", type: "string", description: "The string" },
      { name: "find", type: "string", description: "String to find" },
      { name: "replace", type: "string", description: "Replacement" }
    ],
    example: '["str/replace", "hello world", "world", "there"] // => "hello there"'
  },
  "str/replaceAll": {
    module: "str",
    category: "std-str",
    minArity: 3,
    maxArity: 3,
    description: "Replace all occurrences",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "s", type: "string", description: "The string" },
      { name: "find", type: "string", description: "String to find" },
      { name: "replace", type: "string", description: "Replacement" }
    ],
    example: '["str/replaceAll", "a-b-c", "-", "_"] // => "a_b_c"'
  },
  "str/includes": {
    module: "str",
    category: "std-str",
    minArity: 2,
    maxArity: 2,
    description: "Check if string contains substring",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "s", type: "string", description: "The string" },
      { name: "search", type: "string", description: "Substring to find" }
    ],
    example: '["str/includes", "hello world", "world"] // => true'
  },
  "str/startsWith": {
    module: "str",
    category: "std-str",
    minArity: 2,
    maxArity: 2,
    description: "Check if string starts with prefix",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "s", type: "string", description: "The string" },
      { name: "prefix", type: "string", description: "Prefix to check" }
    ],
    example: '["str/startsWith", "hello", "hel"] // => true'
  },
  "str/endsWith": {
    module: "str",
    category: "std-str",
    minArity: 2,
    maxArity: 2,
    description: "Check if string ends with suffix",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "s", type: "string", description: "The string" },
      { name: "suffix", type: "string", description: "Suffix to check" }
    ],
    example: '["str/endsWith", "hello", "lo"] // => true'
  },
  "str/padStart": {
    module: "str",
    category: "std-str",
    minArity: 2,
    maxArity: 3,
    description: "Pad string from start to target length",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "s", type: "string", description: "The string" },
      { name: "len", type: "number", description: "Target length" },
      { name: "char", type: "string", description: "Padding character", optional: true, defaultValue: " " }
    ],
    example: '["str/padStart", "5", 3, "0"] // => "005"'
  },
  "str/padEnd": {
    module: "str",
    category: "std-str",
    minArity: 2,
    maxArity: 3,
    description: "Pad string from end to target length",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "s", type: "string", description: "The string" },
      { name: "len", type: "number", description: "Target length" },
      { name: "char", type: "string", description: "Padding character", optional: true, defaultValue: " " }
    ],
    example: '["str/padEnd", "5", 3, "0"] // => "500"'
  },
  "str/repeat": {
    module: "str",
    category: "std-str",
    minArity: 2,
    maxArity: 2,
    description: "Repeat string n times",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "s", type: "string", description: "The string" },
      { name: "count", type: "number", description: "Repeat count" }
    ],
    example: '["str/repeat", "ab", 3] // => "ababab"'
  },
  "str/reverse": {
    module: "str",
    category: "std-str",
    minArity: 1,
    maxArity: 1,
    description: "Reverse string",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "s", type: "string", description: "The string" }],
    example: '["str/reverse", "hello"] // => "olleh"'
  },
  "str/capitalize": {
    module: "str",
    category: "std-str",
    minArity: 1,
    maxArity: 1,
    description: "Capitalize first character",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "s", type: "string", description: "The string" }],
    example: '["str/capitalize", "hello"] // => "Hello"'
  },
  "str/titleCase": {
    module: "str",
    category: "std-str",
    minArity: 1,
    maxArity: 1,
    description: "Convert to Title Case",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "s", type: "string", description: "The string" }],
    example: '["str/titleCase", "hello world"] // => "Hello World"'
  },
  "str/camelCase": {
    module: "str",
    category: "std-str",
    minArity: 1,
    maxArity: 1,
    description: "Convert to camelCase",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "s", type: "string", description: "The string" }],
    example: '["str/camelCase", "hello world"] // => "helloWorld"'
  },
  "str/kebabCase": {
    module: "str",
    category: "std-str",
    minArity: 1,
    maxArity: 1,
    description: "Convert to kebab-case",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "s", type: "string", description: "The string" }],
    example: '["str/kebabCase", "Hello World"] // => "hello-world"'
  },
  "str/snakeCase": {
    module: "str",
    category: "std-str",
    minArity: 1,
    maxArity: 1,
    description: "Convert to snake_case",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "s", type: "string", description: "The string" }],
    example: '["str/snakeCase", "Hello World"] // => "hello_world"'
  },
  "str/default": {
    module: "str",
    category: "std-str",
    minArity: 2,
    maxArity: 2,
    description: "Return default if value is null/undefined/empty",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "s", type: "string | null", description: "The value" },
      { name: "default", type: "string", description: "Default value" }
    ],
    example: '["str/default", null, "N/A"] // => "N/A"'
  },
  "str/template": {
    module: "str",
    category: "std-str",
    minArity: 2,
    maxArity: 2,
    description: "Variable substitution in template string",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "template", type: "string", description: "Template with {placeholders}" },
      { name: "vars", type: "object", description: "Variables to substitute" }
    ],
    example: '["str/template", "Hello, {name}!", {"name": "World"}] // => "Hello, World!"'
  },
  "str/truncate": {
    module: "str",
    category: "std-str",
    minArity: 2,
    maxArity: 3,
    description: "Truncate string with optional suffix",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "s", type: "string", description: "The string" },
      { name: "len", type: "number", description: "Maximum length" },
      { name: "suffix", type: "string", description: "Suffix for truncated strings", optional: true, defaultValue: "..." }
    ],
    example: '["str/truncate", "Hello World", 8, "..."] // => "Hello..."'
  }
};

// modules/array.ts
var ARRAY_OPERATORS = {
  "array/len": {
    module: "array",
    category: "std-array",
    minArity: 1,
    maxArity: 1,
    description: "Array length",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "arr", type: "array", description: "The array" }],
    example: '["array/len", [1, 2, 3]] // => 3'
  },
  "array/range": {
    module: "array",
    category: "std-array",
    minArity: 1,
    maxArity: 3,
    description: "Generate numbers: (range n) => [0,n); (range start end) => [start,end); optional step",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "start", type: "number", description: "Start (inclusive); when alone, this is the exclusive end from 0" },
      { name: "end", type: "number", description: "End value (exclusive)" },
      { name: "step", type: "number", description: "Step between values (default 1)" }
    ],
    example: '["array/range", 0, 5] // => [0, 1, 2, 3, 4]'
  },
  "array/first": {
    module: "array",
    category: "std-array",
    minArity: 1,
    maxArity: 1,
    description: "Get first element (element type of input array, may be undefined when empty)",
    hasSideEffects: false,
    returnType: "any",
    returnSemantics: "element-of-arg<0>?",
    params: [{ name: "arr", type: "array", description: "The array" }],
    example: '["array/first", [1, 2, 3]] // => 1'
  },
  "array/last": {
    module: "array",
    category: "std-array",
    minArity: 1,
    maxArity: 1,
    description: "Get last element (element type of input array, may be undefined when empty)",
    hasSideEffects: false,
    returnType: "any",
    returnSemantics: "element-of-arg<0>?",
    params: [{ name: "arr", type: "array", description: "The array" }],
    example: '["array/last", [1, 2, 3]] // => 3'
  },
  "array/nth": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Get element at index (element type of input array, may be undefined out-of-bounds)",
    hasSideEffects: false,
    returnType: "any",
    returnSemantics: "element-of-arg<0>?",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "index", type: "number", description: "Index (0-based)" }
    ],
    example: '["array/nth", [1, 2, 3], 1] // => 2'
  },
  "array/slice": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 3,
    description: "Extract subarray",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "start", type: "number", description: "Start index" },
      { name: "end", type: "number", description: "End index (exclusive)", optional: true }
    ],
    example: '["array/slice", [1, 2, 3, 4], 1, 3] // => [2, 3]'
  },
  "array/concat": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: null,
    description: "Concatenate arrays",
    hasSideEffects: false,
    returnType: "array",
    params: [{ name: "...arrs", type: "array[]", description: "Arrays to concatenate" }],
    example: '["array/concat", [1, 2], [3, 4]] // => [1, 2, 3, 4]'
  },
  "array/append": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Add item to end (returns new array)",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "item", type: "any", description: "Item to add" }
    ],
    example: '["array/append", [1, 2], 3] // => [1, 2, 3]'
  },
  "array/prepend": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Add item to start (returns new array)",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "item", type: "any", description: "Item to add" }
    ],
    example: '["array/prepend", [2, 3], 1] // => [1, 2, 3]'
  },
  "array/insert": {
    module: "array",
    category: "std-array",
    minArity: 3,
    maxArity: 3,
    description: "Insert item at index (returns new array)",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "index", type: "number", description: "Index to insert at" },
      { name: "item", type: "any", description: "Item to insert" }
    ],
    example: '["array/insert", [1, 3], 1, 2] // => [1, 2, 3]'
  },
  "array/remove": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Remove item at index (returns new array)",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "index", type: "number", description: "Index to remove" }
    ],
    example: '["array/remove", [1, 2, 3], 1] // => [1, 3]'
  },
  "array/removeItem": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Remove first matching item (returns new array)",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "item", type: "any", description: "Item to remove" }
    ],
    example: '["array/removeItem", [1, 2, 3, 2], 2] // => [1, 3, 2]'
  },
  "array/reverse": {
    module: "array",
    category: "std-array",
    minArity: 1,
    maxArity: 1,
    description: "Reverse array order (returns new array)",
    hasSideEffects: false,
    returnType: "array",
    params: [{ name: "arr", type: "array", description: "The array" }],
    example: '["array/reverse", [1, 2, 3]] // => [3, 2, 1]'
  },
  "array/sort": {
    module: "array",
    category: "std-array",
    minArity: 1,
    maxArity: 3,
    description: "Sort array (returns new array)",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "key", type: "string", description: "Field to sort by (for objects)", optional: true },
      { name: "dir", type: "string", description: '"asc" or "desc"', optional: true, defaultValue: "asc" }
    ],
    example: '["array/sort", [{"price": 3}, {"price": 9}, {"price": 5}], "price", "desc"]'
  },
  "array/shuffle": {
    module: "array",
    category: "std-array",
    minArity: 1,
    maxArity: 1,
    description: "Randomly shuffle array (returns new array)",
    hasSideEffects: false,
    returnType: "array",
    params: [{ name: "arr", type: "array", description: "The array" }],
    example: '["array/shuffle", [1, 2, 3, 4, 5]]'
  },
  "array/unique": {
    module: "array",
    category: "std-array",
    minArity: 1,
    maxArity: 1,
    description: "Remove duplicates (returns new array)",
    hasSideEffects: false,
    returnType: "array",
    params: [{ name: "arr", type: "array", description: "The array" }],
    example: '["array/unique", [1, 2, 2, 3, 1]] // => [1, 2, 3]'
  },
  "array/flatten": {
    module: "array",
    category: "std-array",
    minArity: 1,
    maxArity: 1,
    description: "Flatten nested arrays one level",
    hasSideEffects: false,
    returnType: "array",
    params: [{ name: "arr", type: "array", description: "The array" }],
    example: '["array/flatten", [[1, 2], [3, 4]]] // => [1, 2, 3, 4]'
  },
  "array/zip": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Pair elements from two arrays",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "arr1", type: "array", description: "First array" },
      { name: "arr2", type: "array", description: "Second array" }
    ],
    example: '["array/zip", [1, 2], ["a", "b"]] // => [[1, "a"], [2, "b"]]'
  },
  "array/includes": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Check if array contains item",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "item", type: "any", description: "Item to find" }
    ],
    example: '["array/includes", [1, 2, 3], 2] // => true'
  },
  "array/indexOf": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Find index of item (-1 if not found)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "item", type: "any", description: "Item to find" }
    ],
    example: '["array/indexOf", [1, 2, 3], 2] // => 1'
  },
  "array/find": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Find first element matching predicate (element type of input array, undefined when no match)",
    hasSideEffects: false,
    returnType: "any",
    returnSemantics: "element-of-arg<0>?",
    acceptsLambda: true,
    lambdaArgPosition: 1,
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "pred", type: "lambda", description: "Predicate function" }
    ],
    example: '["array/find", [{"status": "active"}, {"status": "archived"}], ["fn", "x", ["=", "@x.status", "active"]]]'
  },
  "array/findIndex": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Find index of first element matching predicate (-1 if none)",
    hasSideEffects: false,
    returnType: "number",
    acceptsLambda: true,
    lambdaArgPosition: 1,
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "pred", type: "lambda", description: "Predicate function" }
    ],
    example: '["array/findIndex", [{"status": "active"}, {"status": "archived"}], ["fn", "x", ["=", "@x.status", "active"]]]'
  },
  "array/filter": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Keep elements matching predicate (returns same array element type as input)",
    hasSideEffects: false,
    returnType: "array",
    returnSemantics: "identity-of-arg<0>",
    acceptsLambda: true,
    lambdaArgPosition: 1,
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "pred", type: "lambda", description: "Predicate function" }
    ],
    example: '["array/filter", [{"price": 50}, {"price": 150}], ["fn", "x", [">", "@x.price", 100]]]'
  },
  "array/reject": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Remove elements matching predicate (returns same array element type as input)",
    hasSideEffects: false,
    returnType: "array",
    returnSemantics: "identity-of-arg<0>",
    acceptsLambda: true,
    lambdaArgPosition: 1,
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "pred", type: "lambda", description: "Predicate function" }
    ],
    example: '["array/reject", [{"status": "active"}, {"status": "deleted"}], ["fn", "x", ["=", "@x.status", "deleted"]]]'
  },
  "array/map": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Transform each element (return type wraps the lambda result in an array)",
    hasSideEffects: false,
    returnType: "array",
    returnSemantics: "lambda-result",
    acceptsLambda: true,
    lambdaArgPosition: 1,
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "fn", type: "lambda", description: "Transform function" }
    ],
    example: '["array/map", [{"price": 100}, {"price": 200}], ["fn", "x", ["*", "@x.price", 1.1]]]'
  },
  "array/reduce": {
    module: "array",
    category: "std-array",
    minArity: 3,
    maxArity: 3,
    description: "Reduce array to single value (return type matches the lambda result / init type)",
    hasSideEffects: false,
    returnType: "any",
    returnSemantics: "lambda-result",
    acceptsLambda: true,
    lambdaArgPosition: 2,
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "init", type: "any", description: "Initial accumulator value" },
      { name: "fn", type: "lambda", description: "Reducer function (acc, item) => newAcc" }
    ],
    example: '["array/reduce", [{"price": 10}, {"price": 25}], 0, ["fn", ["acc", "x"], ["+", "@acc", "@x.price"]]]'
  },
  "array/every": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Check if all elements match predicate",
    hasSideEffects: false,
    returnType: "boolean",
    acceptsLambda: true,
    lambdaArgPosition: 1,
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "pred", type: "lambda", description: "Predicate function" }
    ],
    example: '["array/every", [{"price": 5}, {"price": 10}], ["fn", "x", [">", "@x.price", 0]]]'
  },
  "array/some": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Check if any element matches predicate",
    hasSideEffects: false,
    returnType: "boolean",
    acceptsLambda: true,
    lambdaArgPosition: 1,
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "pred", type: "lambda", description: "Predicate function" }
    ],
    example: '["array/some", [{"status": "archived"}, {"status": "active"}], ["fn", "x", ["=", "@x.status", "active"]]]'
  },
  "array/count": {
    module: "array",
    category: "std-array",
    minArity: 1,
    maxArity: 2,
    description: "Count elements (optionally matching predicate)",
    hasSideEffects: false,
    returnType: "number",
    acceptsLambda: true,
    lambdaArgPosition: 1,
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "pred", type: "lambda", description: "Predicate function", optional: true }
    ],
    example: '["array/count", [{"status": "done"}, {"status": "todo"}], ["fn", "t", ["=", "@t.status", "done"]]]'
  },
  "array/sum": {
    module: "array",
    category: "std-array",
    minArity: 1,
    maxArity: 2,
    description: "Sum values (optionally by field)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "key", type: "string", description: "Field to sum", optional: true }
    ],
    example: '["array/sum", [{"price": 10}, {"price": 25}], "price"]'
  },
  "array/avg": {
    module: "array",
    category: "std-array",
    minArity: 1,
    maxArity: 2,
    description: "Average of values (optionally by field)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "key", type: "string", description: "Field to average", optional: true }
    ],
    example: '["array/avg", [{"score": 80}, {"score": 90}], "score"]'
  },
  "array/min": {
    module: "array",
    category: "std-array",
    minArity: 1,
    maxArity: 2,
    description: "Minimum value (optionally by field)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "key", type: "string", description: "Field to compare", optional: true }
    ],
    example: '["array/min", [{"price": 3}, {"price": 9}], "price"]'
  },
  "array/max": {
    module: "array",
    category: "std-array",
    minArity: 1,
    maxArity: 2,
    description: "Maximum value (optionally by field)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "key", type: "string", description: "Field to compare", optional: true }
    ],
    example: '["array/max", [{"price": 3}, {"price": 9}], "price"]'
  },
  "array/groupBy": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Group elements by field value",
    hasSideEffects: false,
    returnType: "any",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "key", type: "string", description: "Field to group by" }
    ],
    example: '["array/groupBy", [{"status": "new"}, {"status": "done"}], "status"]'
  },
  "array/partition": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Split array by predicate into [matches, nonMatches]",
    hasSideEffects: false,
    returnType: "array",
    acceptsLambda: true,
    lambdaArgPosition: 1,
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "pred", type: "lambda", description: "Predicate function" }
    ],
    example: '["array/partition", [{"price": 30}, {"price": 80}], ["fn", "x", [">", "@x.price", 50]]]'
  },
  "array/take": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Take first n elements",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "n", type: "number", description: "Number of elements" }
    ],
    example: '["array/take", [1, 2, 3, 4, 5, 6, 7], 5]'
  },
  "array/drop": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Skip first n elements",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "n", type: "number", description: "Number of elements to skip" }
    ],
    example: '["array/drop", [1, 2, 3, 4, 5, 6, 7], 5]'
  },
  "array/takeLast": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Take last n elements",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "n", type: "number", description: "Number of elements" }
    ],
    example: '["array/takeLast", [1, 2, 3, 4, 5], 3]'
  },
  "array/dropLast": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Skip last n elements",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "arr", type: "array", description: "The array" },
      { name: "n", type: "number", description: "Number of elements to skip" }
    ],
    example: '["array/dropLast", [1, 2, 3, 4, 5], 2]'
  },
  "array/cosine": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: 'Cosine similarity between two numeric vectors, in [-1, 1] (1 = identical direction). The building block for "is this answer semantically the reference answer" checks over embeddings. Pure math \u2014 usable in guards and value position, unlike the async llm/* substrate operators.',
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "a", type: { kind: "array", of: "number" }, description: "First vector" },
      { name: "b", type: { kind: "array", of: "number" }, description: "Second vector" }
    ],
    example: '["array/cosine", [1, 0], [0, 1]]'
  },
  "array/nearest": {
    module: "array",
    category: "std-array",
    minArity: 2,
    maxArity: 2,
    description: "Find the candidate vector nearest a query vector by cosine similarity. Returns { index, score, margin } \u2014 index/score of the best match, and margin (best score minus second-best), which is what makes an abstaining gate possible: a small margin means the top match is not confidently distinguished from the runner-up.",
    hasSideEffects: false,
    returnType: "object",
    params: [
      { name: "query", type: { kind: "array", of: "number" }, description: "Query vector" },
      { name: "candidates", type: { kind: "array", of: { kind: "array", of: "number" } }, description: "Candidate vectors to search" }
    ],
    example: '["array/nearest", [1, 0], [[1, 0], [0, 1]]]'
  }
};

// modules/object.ts
var OBJECT_OPERATORS = {
  "object/keys": {
    module: "object",
    category: "std-object",
    minArity: 1,
    maxArity: 1,
    description: "Get object keys as array",
    hasSideEffects: false,
    returnType: "array",
    params: [{ name: "obj", type: "object", description: "The object" }],
    example: '["object/keys", {"a": 1, "b": 2}] // => ["a", "b"]'
  },
  "object/values": {
    module: "object",
    category: "std-object",
    minArity: 1,
    maxArity: 1,
    description: "Get object values as array",
    hasSideEffects: false,
    returnType: "array",
    params: [{ name: "obj", type: "object", description: "The object" }],
    example: '["object/values", {"a": 1, "b": 2}] // => [1, 2]'
  },
  "object/entries": {
    module: "object",
    category: "std-object",
    minArity: 1,
    maxArity: 1,
    description: "Get [key, value] pairs as array",
    hasSideEffects: false,
    returnType: "array",
    params: [{ name: "obj", type: "object", description: "The object" }],
    example: '["object/entries", {"a": 1}] // => [["a", 1]]'
  },
  "object/fromEntries": {
    module: "object",
    category: "std-object",
    minArity: 1,
    maxArity: 1,
    description: "Create object from [key, value] pairs",
    hasSideEffects: false,
    returnType: "any",
    params: [{ name: "entries", type: "array", description: "Array of [key, value] pairs" }],
    example: '["object/fromEntries", [["a", 1], ["b", 2]]] // => {"a": 1, "b": 2}'
  },
  "object/get": {
    module: "object",
    category: "std-object",
    minArity: 2,
    maxArity: 3,
    description: "Get nested value by path (return type derived from input object shape and key)",
    hasSideEffects: false,
    returnType: "any",
    returnSemantics: "object-key-lookup",
    params: [
      { name: "obj", type: "object", description: "The object" },
      { name: "path", type: "string", description: 'Dot-separated path (e.g., "user.name")' },
      { name: "default", type: "any", description: "Default if path not found", optional: true }
    ],
    example: '["object/get", {"profile": {"name": "Ann"}}, "profile.name", "Anonymous"]'
  },
  "object/set": {
    module: "object",
    category: "std-object",
    minArity: 3,
    maxArity: 3,
    description: "Set nested value by path (returns new object)",
    hasSideEffects: false,
    returnType: "any",
    params: [
      { name: "obj", type: "object", description: "The object" },
      { name: "path", type: "string", description: "Dot-separated path" },
      { name: "value", type: "any", description: "Value to set" }
    ],
    example: '["object/set", {"profile": {"name": "Ann"}}, "profile.name", "John"]'
  },
  "object/has": {
    module: "object",
    category: "std-object",
    minArity: 2,
    maxArity: 2,
    description: "Check if path exists",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "obj", type: "object", description: "The object" },
      { name: "path", type: "string", description: "Dot-separated path" }
    ],
    example: '["object/has", {"profile": {"name": "Ann"}}, "profile.name"]'
  },
  "object/merge": {
    module: "object",
    category: "std-object",
    minArity: 2,
    maxArity: null,
    description: "Shallow merge objects (later wins)",
    hasSideEffects: false,
    returnType: "any",
    params: [{ name: "...objs", type: "object[]", description: "Objects to merge" }],
    example: '["object/merge", {"a": 1}, {"b": 2}] // => {"a": 1, "b": 2}'
  },
  "object/deepMerge": {
    module: "object",
    category: "std-object",
    minArity: 2,
    maxArity: null,
    description: "Deep merge objects (later wins)",
    hasSideEffects: false,
    returnType: "any",
    params: [{ name: "...objs", type: "object[]", description: "Objects to merge" }],
    example: '["object/deepMerge", {"a": {"b": 1}}, {"a": {"c": 2}}]'
  },
  "object/pick": {
    module: "object",
    category: "std-object",
    minArity: 2,
    maxArity: 2,
    description: "Select only specified keys (return type is subset of input object shape)",
    hasSideEffects: false,
    returnType: "any",
    returnSemantics: "object-key-lookup",
    params: [
      { name: "obj", type: "object", description: "The object" },
      { name: "keys", type: "array", description: "Keys to keep" }
    ],
    example: '["object/pick", {"name": "Ann", "email": "ann@example.com", "age": 30}, ["name", "email"]]'
  },
  "object/omit": {
    module: "object",
    category: "std-object",
    minArity: 2,
    maxArity: 2,
    description: "Exclude specified keys",
    hasSideEffects: false,
    returnType: "any",
    params: [
      { name: "obj", type: "object", description: "The object" },
      { name: "keys", type: "array", description: "Keys to exclude" }
    ],
    example: '["object/omit", {"name": "Ann", "password": "x"}, ["password"]]'
  },
  "object/mapValues": {
    module: "object",
    category: "std-object",
    minArity: 2,
    maxArity: 2,
    description: "Transform all values",
    hasSideEffects: false,
    returnType: "any",
    acceptsLambda: true,
    lambdaArgPosition: 1,
    params: [
      { name: "obj", type: "object", description: "The object" },
      { name: "fn", type: "lambda", description: "Transform function" }
    ],
    example: '["object/mapValues", {"a": 0.5, "b": 0.25}, ["fn", "v", ["*", "@v", 100]]]'
  },
  "object/mapKeys": {
    module: "object",
    category: "std-object",
    minArity: 2,
    maxArity: 2,
    description: "Transform all keys",
    hasSideEffects: false,
    returnType: "any",
    acceptsLambda: true,
    lambdaArgPosition: 1,
    params: [
      { name: "obj", type: "object", description: "The object" },
      { name: "fn", type: "lambda", description: "Transform function" }
    ],
    example: '["object/mapKeys", {"a": 1, "b": 2}, ["fn", "k", ["str/upper", "@k"]]]'
  },
  "object/filter": {
    module: "object",
    category: "std-object",
    minArity: 2,
    maxArity: 2,
    description: "Filter entries by predicate",
    hasSideEffects: false,
    returnType: "any",
    acceptsLambda: true,
    lambdaArgPosition: 1,
    params: [
      { name: "obj", type: "object", description: "The object" },
      { name: "pred", type: "lambda", description: "Predicate (key, value) => boolean" }
    ],
    example: '["object/filter", {"a": 1, "b": null}, ["fn", ["k", "v"], ["!=", "@v", null]]]'
  },
  "object/equals": {
    module: "object",
    category: "std-object",
    minArity: 2,
    maxArity: 2,
    description: "Deep equality check",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "a", type: "object", description: "First object" },
      { name: "b", type: "object", description: "Second object" }
    ],
    example: '["object/equals", {"a": 1}, {"a": 1}] // => true'
  },
  "object/clone": {
    module: "object",
    category: "std-object",
    minArity: 1,
    maxArity: 1,
    description: "Shallow clone object",
    hasSideEffects: false,
    returnType: "any",
    params: [{ name: "obj", type: "object", description: "The object" }],
    example: '["object/clone", {"a": 1, "b": 2}]'
  },
  "object/deepClone": {
    module: "object",
    category: "std-object",
    minArity: 1,
    maxArity: 1,
    description: "Deep clone object",
    hasSideEffects: false,
    returnType: "any",
    params: [{ name: "obj", type: "object", description: "The object" }],
    example: '["object/deepClone", {"a": {"b": 2}}]'
  },
  "path": {
    module: "object",
    category: "std-object",
    minArity: 1,
    maxArity: null,
    // variadic
    description: "Build a dot-separated path string from segments. Used with set effect for dynamic field paths.",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "...segments", type: "string[]", description: "Path segments to join with dots" }
    ],
    example: '["path", "formValues", "customerName"] // => "formValues.customerName"'
  }
};

// modules/json.ts
var JSON_OPERATORS = {
  "json/parse": {
    module: "json",
    category: "std-json",
    minArity: 1,
    maxArity: 1,
    description: "Parse a JSON string into a value; invalid JSON or non-string input returns null (never throws)",
    hasSideEffects: false,
    returnType: "any",
    params: [{ name: "s", type: "string", description: "The JSON string to parse" }],
    example: '["json/parse", "{\\"a\\": 1}"] // => {a: 1}'
  },
  "json/stringify": {
    module: "json",
    category: "std-json",
    minArity: 1,
    maxArity: 1,
    description: "Serialize any value to a JSON string",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "v", type: "any", description: "The value to serialize" }],
    example: '["json/stringify", {a: 1}] // => "{\\"a\\":1}"'
  }
};

// modules/time.ts
var TIME_OPERATORS = {
  "time/now": {
    module: "time",
    category: "std-time",
    minArity: 0,
    maxArity: 0,
    description: "Current timestamp",
    hasSideEffects: false,
    returnType: "number",
    params: [],
    example: '["time/now"] // => 1705593600000'
  },
  "time/today": {
    module: "time",
    category: "std-time",
    minArity: 0,
    maxArity: 0,
    description: "Today at midnight (local time)",
    hasSideEffects: false,
    returnType: "number",
    params: [],
    example: '["time/today"]'
  },
  "time/parse": {
    module: "time",
    category: "std-time",
    minArity: 1,
    maxArity: 2,
    description: "Parse string to timestamp",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "str", type: "string", description: "Date string" },
      { name: "format", type: "string", description: "Format pattern", optional: true }
    ],
    example: '["time/parse", "2024-01-18", "YYYY-MM-DD"]'
  },
  "time/format": {
    module: "time",
    category: "std-time",
    minArity: 2,
    maxArity: 2,
    description: "Format timestamp to string",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "date", type: "number", description: "Timestamp" },
      { name: "format", type: "string", description: "Format pattern" }
    ],
    example: '["time/format", 1700000000000, "MMM DD, YYYY"]'
  },
  "time/year": {
    module: "time",
    category: "std-time",
    minArity: 1,
    maxArity: 1,
    description: "Get year from timestamp",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "date", type: "number", description: "Timestamp" }],
    example: '["time/year", 1700000000000] // => 2023'
  },
  "time/month": {
    module: "time",
    category: "std-time",
    minArity: 1,
    maxArity: 1,
    description: "Get month from timestamp (1-12)",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "date", type: "number", description: "Timestamp" }],
    example: '["time/month", 1700000000000] // => 11'
  },
  "time/day": {
    module: "time",
    category: "std-time",
    minArity: 1,
    maxArity: 1,
    description: "Get day of month from timestamp (1-31)",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "date", type: "number", description: "Timestamp" }],
    example: '["time/day", 1700000000000] // => 14'
  },
  "time/weekday": {
    module: "time",
    category: "std-time",
    minArity: 1,
    maxArity: 1,
    description: "Get day of week (0=Sunday, 6=Saturday)",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "date", type: "number", description: "Timestamp" }],
    example: '["time/weekday", 1700000000000] // => 2 (Tuesday)'
  },
  "time/hour": {
    module: "time",
    category: "std-time",
    minArity: 1,
    maxArity: 1,
    description: "Get hour from timestamp (0-23)",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "date", type: "number", description: "Timestamp" }],
    example: '["time/hour", 1700000000000]'
  },
  "time/minute": {
    module: "time",
    category: "std-time",
    minArity: 1,
    maxArity: 1,
    description: "Get minute from timestamp (0-59)",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "date", type: "number", description: "Timestamp" }],
    example: '["time/minute", 1700000000000]'
  },
  "time/second": {
    module: "time",
    category: "std-time",
    minArity: 1,
    maxArity: 1,
    description: "Get second from timestamp (0-59)",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "date", type: "number", description: "Timestamp" }],
    example: '["time/second", 1700000000000]'
  },
  "time/add": {
    module: "time",
    category: "std-time",
    minArity: 3,
    maxArity: 3,
    description: "Add time to timestamp",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "date", type: "number", description: "Timestamp" },
      { name: "amount", type: "number", description: "Amount to add" },
      { name: "unit", type: "string", description: "Time unit (year/month/week/day/hour/minute/second/ms)" }
    ],
    example: '["time/add", ["time/now"], 7, "day"]'
  },
  "time/subtract": {
    module: "time",
    category: "std-time",
    minArity: 3,
    maxArity: 3,
    description: "Subtract time from timestamp",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "date", type: "number", description: "Timestamp" },
      { name: "amount", type: "number", description: "Amount to subtract" },
      { name: "unit", type: "string", description: "Time unit" }
    ],
    example: '["time/subtract", ["time/now"], 1, "hour"]'
  },
  "time/diff": {
    module: "time",
    category: "std-time",
    minArity: 2,
    maxArity: 3,
    description: "Difference between timestamps",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "a", type: "number", description: "First timestamp" },
      { name: "b", type: "number", description: "Second timestamp" },
      { name: "unit", type: "string", description: "Result unit", optional: true, defaultValue: "ms" }
    ],
    example: '["time/diff", 1700000000000, ["time/now"], "year"]'
  },
  "time/startOf": {
    module: "time",
    category: "std-time",
    minArity: 2,
    maxArity: 2,
    description: "Get start of time period",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "date", type: "number", description: "Timestamp" },
      { name: "unit", type: "string", description: "Time unit (year/month/week/day/hour/minute)" }
    ],
    example: '["time/startOf", ["time/now"], "month"]'
  },
  "time/endOf": {
    module: "time",
    category: "std-time",
    minArity: 2,
    maxArity: 2,
    description: "Get end of time period",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "date", type: "number", description: "Timestamp" },
      { name: "unit", type: "string", description: "Time unit" }
    ],
    example: '["time/endOf", ["time/now"], "month"]'
  },
  "time/isBefore": {
    module: "time",
    category: "std-time",
    minArity: 2,
    maxArity: 2,
    description: "Check if a is before b",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "a", type: "number", description: "First timestamp" },
      { name: "b", type: "number", description: "Second timestamp" }
    ],
    example: '["time/isBefore", 1700000000000, 1700086400000]'
  },
  "time/isAfter": {
    module: "time",
    category: "std-time",
    minArity: 2,
    maxArity: 2,
    description: "Check if a is after b",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "a", type: "number", description: "First timestamp" },
      { name: "b", type: "number", description: "Second timestamp" }
    ],
    example: '["time/isAfter", ["time/now"], 1700000000000]'
  },
  "time/isBetween": {
    module: "time",
    category: "std-time",
    minArity: 3,
    maxArity: 3,
    description: "Check if date is between start and end",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "date", type: "number", description: "Timestamp to check" },
      { name: "start", type: "number", description: "Range start" },
      { name: "end", type: "number", description: "Range end" }
    ],
    example: '["time/isBetween", ["time/now"], 1700000000000, 2000000000000]'
  },
  "time/isSame": {
    module: "time",
    category: "std-time",
    minArity: 2,
    maxArity: 3,
    description: "Check if timestamps are same (optionally by unit)",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "a", type: "number", description: "First timestamp" },
      { name: "b", type: "number", description: "Second timestamp" },
      { name: "unit", type: "string", description: "Comparison unit", optional: true }
    ],
    example: '["time/isSame", 1700000000000, 1700001000000, "day"]'
  },
  "time/isPast": {
    module: "time",
    category: "std-time",
    minArity: 1,
    maxArity: 1,
    description: "Check if timestamp is in the past",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "date", type: "number", description: "Timestamp" }],
    example: '["time/isPast", 1700000000000]'
  },
  "time/isFuture": {
    module: "time",
    category: "std-time",
    minArity: 1,
    maxArity: 1,
    description: "Check if timestamp is in the future",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "date", type: "number", description: "Timestamp" }],
    example: '["time/isFuture", 2000000000000]'
  },
  "time/isToday": {
    module: "time",
    category: "std-time",
    minArity: 1,
    maxArity: 1,
    description: "Check if timestamp is today",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "date", type: "number", description: "Timestamp" }],
    example: '["time/isToday", ["time/now"]]'
  },
  "time/relative": {
    module: "time",
    category: "std-time",
    minArity: 1,
    maxArity: 1,
    description: 'Format as relative time ("2 hours ago", "in 3 days")',
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "date", type: "number", description: "Timestamp" }],
    example: '["time/relative", 1700000000000] // => "2 years ago"'
  },
  "time/duration": {
    module: "time",
    category: "std-time",
    minArity: 1,
    maxArity: 1,
    description: 'Format milliseconds as duration ("2h 30m")',
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "ms", type: "number", description: "Duration in milliseconds" }],
    example: '["time/duration", 9000000] // => "2h 30m"'
  }
};

// modules/validate.ts
var VALIDATE_OPERATORS = {
  "validate/required": {
    module: "validate",
    category: "std-validate",
    minArity: 1,
    maxArity: 1,
    description: "Check if value is not null, undefined, or empty string",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "value", type: "any", description: "Value to check" }],
    example: '["validate/required", "Ann"]'
  },
  "validate/string": {
    module: "validate",
    category: "std-validate",
    minArity: 1,
    maxArity: 1,
    description: "Check if value is a string",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "value", type: "any", description: "Value to check" }],
    example: '["validate/string", "Ann"]'
  },
  "validate/number": {
    module: "validate",
    category: "std-validate",
    minArity: 1,
    maxArity: 1,
    description: "Check if value is a number",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "value", type: "any", description: "Value to check" }],
    example: '["validate/number", 42]'
  },
  "validate/boolean": {
    module: "validate",
    category: "std-validate",
    minArity: 1,
    maxArity: 1,
    description: "Check if value is a boolean",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "value", type: "any", description: "Value to check" }],
    example: '["validate/boolean", true]'
  },
  "validate/array": {
    module: "validate",
    category: "std-validate",
    minArity: 1,
    maxArity: 1,
    description: "Check if value is an array",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "value", type: "any", description: "Value to check" }],
    example: '["validate/array", [1, 2]]'
  },
  "validate/object": {
    module: "validate",
    category: "std-validate",
    minArity: 1,
    maxArity: 1,
    description: "Check if value is an object",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "value", type: "any", description: "Value to check" }],
    example: '["validate/object", {"a": 1}]'
  },
  "validate/email": {
    module: "validate",
    category: "std-validate",
    minArity: 1,
    maxArity: 1,
    description: "Check if value is a valid email format",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "value", type: "string", description: "Email to validate" }],
    example: '["validate/email", "ann@example.com"]'
  },
  "validate/url": {
    module: "validate",
    category: "std-validate",
    minArity: 1,
    maxArity: 1,
    description: "Check if value is a valid URL format",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "value", type: "string", description: "URL to validate" }],
    example: '["validate/url", "https://example.com"]'
  },
  "validate/uuid": {
    module: "validate",
    category: "std-validate",
    minArity: 1,
    maxArity: 1,
    description: "Check if value is a valid UUID",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "value", type: "string", description: "UUID to validate" }],
    example: '["validate/uuid", "550e8400-e29b-41d4-a716-446655440000"]'
  },
  "validate/phone": {
    module: "validate",
    category: "std-validate",
    minArity: 1,
    maxArity: 1,
    description: "Check if value is a valid phone number",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "value", type: "string", description: "Phone number to validate" }],
    example: '["validate/phone", "+15551234567"]'
  },
  "validate/creditCard": {
    module: "validate",
    category: "std-validate",
    minArity: 1,
    maxArity: 1,
    description: "Check if value is a valid credit card number (Luhn algorithm)",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "value", type: "string", description: "Card number to validate" }],
    example: '["validate/creditCard", "4111111111111111"]'
  },
  "validate/date": {
    module: "validate",
    category: "std-validate",
    minArity: 1,
    maxArity: 1,
    description: "Check if value is a valid date",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "value", type: "any", description: "Value to check" }],
    example: '["validate/date", "2024-01-18"]'
  },
  "validate/minLength": {
    module: "validate",
    category: "std-validate",
    minArity: 2,
    maxArity: 2,
    description: "Check if string/array has minimum length",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "value", type: "string | array", description: "Value to check" },
      { name: "min", type: "number", description: "Minimum length" }
    ],
    example: '["validate/minLength", "password123", 8]'
  },
  "validate/maxLength": {
    module: "validate",
    category: "std-validate",
    minArity: 2,
    maxArity: 2,
    description: "Check if string/array has maximum length",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "value", type: "string | array", description: "Value to check" },
      { name: "max", type: "number", description: "Maximum length" }
    ],
    example: '["validate/maxLength", "Ann", 50]'
  },
  "validate/length": {
    module: "validate",
    category: "std-validate",
    minArity: 2,
    maxArity: 2,
    description: "Check if string/array has exact length",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "value", type: "string | array", description: "Value to check" },
      { name: "exact", type: "number", description: "Required length" }
    ],
    example: '["validate/length", "ABC123", 6]'
  },
  "validate/min": {
    module: "validate",
    category: "std-validate",
    minArity: 2,
    maxArity: 2,
    description: "Check if number is >= minimum",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "value", type: "number", description: "Number to check" },
      { name: "min", type: "number", description: "Minimum value" }
    ],
    example: '["validate/min", 21, 18]'
  },
  "validate/max": {
    module: "validate",
    category: "std-validate",
    minArity: 2,
    maxArity: 2,
    description: "Check if number is <= maximum",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "value", type: "number", description: "Number to check" },
      { name: "max", type: "number", description: "Maximum value" }
    ],
    example: '["validate/max", 50, 100]'
  },
  "validate/range": {
    module: "validate",
    category: "std-validate",
    minArity: 3,
    maxArity: 3,
    description: "Check if number is within range [min, max]",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "value", type: "number", description: "Number to check" },
      { name: "min", type: "number", description: "Minimum value" },
      { name: "max", type: "number", description: "Maximum value" }
    ],
    example: '["validate/range", 4, 1, 5]'
  },
  "validate/pattern": {
    module: "validate",
    category: "std-validate",
    minArity: 2,
    maxArity: 2,
    description: "Check if string matches regex pattern",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "value", type: "string", description: "String to check" },
      { name: "regex", type: "string", description: "Regex pattern" }
    ],
    example: '["validate/pattern", "ABC123", "^[A-Z]{3}[0-9]{3}$"]'
  },
  "validate/oneOf": {
    module: "validate",
    category: "std-validate",
    minArity: 2,
    maxArity: 2,
    description: "Check if value is in list of allowed values",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "value", type: "any", description: "Value to check" },
      { name: "options", type: "array", description: "Allowed values" }
    ],
    example: '["validate/oneOf", "admin", ["admin", "user", "guest"]]'
  },
  "validate/noneOf": {
    module: "validate",
    category: "std-validate",
    minArity: 2,
    maxArity: 2,
    description: "Check if value is not in list of disallowed values",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "value", type: "any", description: "Value to check" },
      { name: "options", type: "array", description: "Disallowed values" }
    ],
    example: '["validate/noneOf", "alice", ["admin", "root", "system"]]'
  },
  "validate/equals": {
    module: "validate",
    category: "std-validate",
    minArity: 2,
    maxArity: 2,
    description: "Deep equality check",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "a", type: "any", description: "First value" },
      { name: "b", type: "any", description: "Second value" }
    ],
    example: '["validate/equals", "secret", "secret"]'
  },
  "validate/check": {
    module: "validate",
    category: "std-validate",
    minArity: 2,
    maxArity: 2,
    description: "Run multiple validation rules, return { valid, errors }",
    hasSideEffects: false,
    returnType: "any",
    params: [
      { name: "value", type: "any", description: "Value or object to validate" },
      { name: "rules", type: "object", description: "Validation rules by field" }
    ],
    example: '["validate/check", {"name": "Ann", "email": "ann@example.com", "age": 20}, {"name": [["required"], ["minLength", 2], ["maxLength", 50]], "email": [["required"], ["email"]], "age": [["number"]]}]'
  }
};

// modules/format.ts
var FORMAT_OPERATORS = {
  "format/number": {
    module: "format",
    category: "std-format",
    minArity: 1,
    maxArity: 2,
    description: "Format number with locale-aware separators",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "n", type: "number", description: "Number to format" },
      { name: "opts", type: "object", description: "Format options (decimals, locale)", optional: true }
    ],
    example: '["format/number", 1234567.89] // => "1,234,567.89"'
  },
  "format/currency": {
    module: "format",
    category: "std-format",
    minArity: 2,
    maxArity: 3,
    description: "Format as currency",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "n", type: "number", description: "Amount" },
      { name: "currency", type: "string", description: "Currency code (USD, EUR, etc.)" },
      { name: "locale", type: "string", description: "Locale", optional: true, defaultValue: "en-US" }
    ],
    example: '["format/currency", 1234.56, "USD"] // => "$1,234.56"'
  },
  "format/percent": {
    module: "format",
    category: "std-format",
    minArity: 1,
    maxArity: 2,
    description: "Format as percentage",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "n", type: "number", description: "Number (0.5 = 50%)" },
      { name: "decimals", type: "number", description: "Decimal places", optional: true, defaultValue: 0 }
    ],
    example: '["format/percent", 0.856, 1] // => "85.6%"'
  },
  "format/bytes": {
    module: "format",
    category: "std-format",
    minArity: 1,
    maxArity: 1,
    description: "Format bytes as human-readable size",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "n", type: "number", description: "Bytes" }],
    example: '["format/bytes", 2500000] // => "2.4 MB"'
  },
  "format/ordinal": {
    module: "format",
    category: "std-format",
    minArity: 1,
    maxArity: 1,
    description: "Format number as ordinal (1st, 2nd, 3rd)",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "n", type: "number", description: "Number" }],
    example: '["format/ordinal", 42] // => "42nd"'
  },
  "format/plural": {
    module: "format",
    category: "std-format",
    minArity: 3,
    maxArity: 3,
    description: "Format count with singular/plural word",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "n", type: "number", description: "Count" },
      { name: "singular", type: "string", description: "Singular form" },
      { name: "plural", type: "string", description: "Plural form" }
    ],
    example: '["format/plural", 5, "item", "items"] // => "5 items"'
  },
  "format/list": {
    module: "format",
    category: "std-format",
    minArity: 1,
    maxArity: 2,
    description: "Format array as natural language list",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "arr", type: "array", description: "Array of strings" },
      { name: "style", type: "string", description: '"and" or "or"', optional: true, defaultValue: "and" }
    ],
    example: '["format/list", ["Alice", "Bob", "Charlie"], "and"] // => "Alice, Bob, and Charlie"'
  },
  "format/phone": {
    module: "format",
    category: "std-format",
    minArity: 1,
    maxArity: 2,
    description: "Format phone number",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "str", type: "string", description: "Phone number digits" },
      { name: "format", type: "string", description: "Format pattern", optional: true, defaultValue: "US" }
    ],
    example: '["format/phone", "5551234567"] // => "(555) 123-4567"'
  },
  "format/creditCard": {
    module: "format",
    category: "std-format",
    minArity: 1,
    maxArity: 1,
    description: "Format credit card with masked digits",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "str", type: "string", description: "Card number" }],
    example: '["format/creditCard", "4111111111111234"] // => "\u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 1234"'
  }
};

// modules/async.ts
var ASYNC_OPERATORS = {
  "async/delay": {
    module: "async",
    category: "std-async",
    minArity: 1,
    maxArity: 2,
    description: "Wait for specified milliseconds, optionally execute an effect after",
    hasSideEffects: true,
    returnType: "any",
    params: [
      { name: "ms", type: "number", description: "Milliseconds to wait" },
      { name: "effect", type: "expression", description: "Optional effect to execute after delay" }
    ],
    example: '["async/delay", 2000, ["emit", "RETRY"]] // Wait 2s then emit'
  },
  "async/interval": {
    module: "async",
    category: "std-async",
    minArity: 2,
    maxArity: 2,
    description: "Execute an effect periodically at a fixed interval",
    hasSideEffects: true,
    returnType: "string",
    params: [
      { name: "ms", type: "number", description: "Interval in milliseconds" },
      { name: "effect", type: "expression", description: "Effect to execute each interval" }
    ],
    example: '["async/interval", 5000, ["emit", "POLL_TICK"]] // Emit every 5s'
  },
  "async/timeout": {
    module: "async",
    category: "std-async",
    minArity: 2,
    maxArity: 2,
    description: "Add timeout to an effect",
    hasSideEffects: true,
    returnType: "any",
    params: [
      { name: "effect", type: "expression", description: "Effect to execute" },
      { name: "ms", type: "number", description: "Timeout in milliseconds" }
    ],
    example: '["async/timeout", ["call", "api", "fetchData"], 5000]'
  },
  "async/debounce": {
    module: "async",
    category: "std-async",
    minArity: 2,
    maxArity: 2,
    description: "Debounce an event (wait for pause in events)",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "event", type: "string", description: "Event name to emit" },
      { name: "ms", type: "number", description: "Debounce delay in milliseconds" }
    ],
    example: '["async/debounce", "SEARCH", 300]'
  },
  "async/throttle": {
    module: "async",
    category: "std-async",
    minArity: 2,
    maxArity: 2,
    description: "Throttle an event (emit at most once per interval)",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "event", type: "string", description: "Event name to emit" },
      { name: "ms", type: "number", description: "Throttle interval in milliseconds" }
    ],
    example: '["async/throttle", "SCROLL", 100]'
  },
  "async/retry": {
    module: "async",
    category: "std-async",
    minArity: 2,
    maxArity: 2,
    description: "Retry an effect with configurable backoff",
    hasSideEffects: true,
    returnType: "any",
    params: [
      { name: "effect", type: "expression", description: "Effect to retry" },
      { name: "opts", type: "object", description: "{ attempts, backoff, baseDelay }" }
    ],
    example: `["async/retry",
  ["call", "api", "fetchData", { "id": "@entity.id" }],
  { "attempts": 3, "backoff": "exponential", "baseDelay": 1000 }]`
  },
  "async/race": {
    module: "async",
    category: "std-async",
    minArity: 2,
    maxArity: null,
    description: "Execute effects in parallel, return first to complete",
    hasSideEffects: true,
    returnType: "any",
    params: [{ name: "...effects", type: "expression[]", description: "Effects to race" }],
    example: '["async/race", ["call", "api1"], ["call", "api2"]]'
  },
  "async/all": {
    module: "async",
    category: "std-async",
    minArity: 2,
    maxArity: null,
    description: "Execute effects in parallel, wait for all to complete",
    hasSideEffects: true,
    returnType: "array",
    params: [{ name: "...effects", type: "expression[]", description: "Effects to execute" }],
    example: '["async/all", ["call", "api1"], ["call", "api2"]]'
  },
  "async/sequence": {
    module: "async",
    category: "std-async",
    minArity: 2,
    maxArity: null,
    description: "Execute effects in sequence (one after another)",
    hasSideEffects: true,
    returnType: "array",
    params: [{ name: "...effects", type: "expression[]", description: "Effects to execute in order" }],
    example: '["async/sequence", ["call", "validate"], ["call", "save"]]'
  }
};

// modules/nn.ts
var NN_OPERATORS = {
  "nn/sequential": {
    module: "nn",
    category: "ml-arch",
    minArity: 1,
    maxArity: null,
    description: "Create a sequential neural network from layers",
    hasSideEffects: false,
    returnType: "nn/module",
    params: [
      { name: "...layers", type: "nn/layer[]", description: "Layers to stack sequentially" }
    ],
    example: '["nn/sequential", ["nn/linear", 16, 64], ["nn/relu"], ["nn/linear", 64, 4]]'
  },
  "nn/linear": {
    module: "nn",
    category: "ml-arch",
    minArity: 2,
    maxArity: 2,
    description: "Fully connected linear layer (output = input * weights + bias)",
    hasSideEffects: false,
    returnType: "nn/layer",
    params: [
      { name: "inFeatures", type: "number", description: "Input dimension" },
      { name: "outFeatures", type: "number", description: "Output dimension" }
    ],
    example: '["nn/linear", 16, 64] // 16 inputs -> 64 outputs'
  },
  "nn/relu": {
    module: "nn",
    category: "ml-arch",
    minArity: 0,
    maxArity: 0,
    description: "ReLU activation function: max(0, x)",
    hasSideEffects: false,
    returnType: "nn/layer",
    params: [],
    example: '["nn/relu"]'
  },
  "nn/tanh": {
    module: "nn",
    category: "ml-arch",
    minArity: 0,
    maxArity: 0,
    description: "Tanh activation function: (e^x - e^-x) / (e^x + e^-x)",
    hasSideEffects: false,
    returnType: "nn/layer",
    params: [],
    example: '["nn/tanh"]'
  },
  "nn/sigmoid": {
    module: "nn",
    category: "ml-arch",
    minArity: 0,
    maxArity: 0,
    description: "Sigmoid activation function: 1 / (1 + e^-x)",
    hasSideEffects: false,
    returnType: "nn/layer",
    params: [],
    example: '["nn/sigmoid"]'
  },
  "nn/softmax": {
    module: "nn",
    category: "ml-arch",
    minArity: 0,
    maxArity: 1,
    description: "Softmax activation function (normalizes to probability distribution)",
    hasSideEffects: false,
    returnType: "nn/layer",
    params: [
      { name: "dim", type: "number", description: "Dimension to apply softmax", optional: true, defaultValue: -1 }
    ],
    example: '["nn/softmax"]'
  },
  "nn/dropout": {
    module: "nn",
    category: "ml-arch",
    minArity: 0,
    maxArity: 1,
    description: "Dropout layer for regularization (randomly zeros elements during training)",
    hasSideEffects: false,
    returnType: "nn/layer",
    params: [
      { name: "p", type: "number", description: "Dropout probability", optional: true, defaultValue: 0.5 }
    ],
    example: '["nn/dropout", 0.3] // 30% dropout'
  },
  "nn/batchnorm": {
    module: "nn",
    category: "ml-arch",
    minArity: 1,
    maxArity: 1,
    description: "Batch normalization layer",
    hasSideEffects: false,
    returnType: "nn/layer",
    params: [
      { name: "numFeatures", type: "number", description: "Number of features to normalize" }
    ],
    example: '["nn/batchnorm", 64]'
  },
  "nn/layernorm": {
    module: "nn",
    category: "ml-arch",
    minArity: 1,
    maxArity: 1,
    description: "Layer normalization",
    hasSideEffects: false,
    returnType: "nn/layer",
    params: [
      { name: "normalizedShape", type: "number | number[]", description: "Shape to normalize over" }
    ],
    example: '["nn/layernorm", 64]'
  },
  "nn/forward": {
    module: "nn",
    category: "ml-effect",
    minArity: 2,
    maxArity: 2,
    description: "Execute forward pass through network",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "module", type: "nn/module", description: "The neural network module" },
      { name: "input", type: "tensor", description: "Input tensor" }
    ],
    example: '["nn/forward", {"layers": [{"type": "dense", "units": 2}]}, [0.1, 0.2]]'
  },
  "nn/getWeights": {
    module: "nn",
    category: "ml-arch",
    minArity: 1,
    maxArity: 1,
    description: "Get network weights as a flat tensor",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "module", type: "nn/module", description: "The neural network module" }
    ],
    example: '["nn/getWeights", {"layers": [{"type": "dense", "units": 2}]}]'
  },
  "nn/setWeights": {
    module: "nn",
    category: "ml-arch",
    minArity: 2,
    maxArity: 2,
    description: "Set network weights from a flat tensor",
    hasSideEffects: true,
    returnType: "nn/module",
    params: [
      { name: "module", type: "nn/module", description: "The neural network module" },
      { name: "weights", type: "tensor", description: "New weights as flat tensor" }
    ],
    example: '["nn/setWeights", {"layers": [{"type": "dense", "units": 2}]}, [0.1, 0.2, 0.3]]'
  },
  "nn/paramCount": {
    module: "nn",
    category: "ml-arch",
    minArity: 1,
    maxArity: 1,
    description: "Get total number of trainable parameters",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "module", type: "nn/module", description: "The neural network module" }
    ],
    example: '["nn/paramCount", {"layers": [{"type": "dense", "units": 2}]}]'
  },
  "nn/clone": {
    module: "nn",
    category: "ml-arch",
    minArity: 1,
    maxArity: 1,
    description: "Create a deep copy of the network with same weights",
    hasSideEffects: false,
    returnType: "nn/module",
    params: [
      { name: "module", type: "nn/module", description: "The neural network module to clone" }
    ],
    example: '["nn/clone", {"layers": [{"type": "dense", "units": 2}]}]'
  },
  // ============================================================================
  // ML lifecycle effect (bare alias invoked by std ML behaviors)
  // ============================================================================
  "forward": {
    module: "nn",
    category: "ml-effect",
    minArity: 2,
    maxArity: 2,
    description: "Run a forward pass through a model (ML effect)",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "model", type: "nn/module", description: "The neural network module" },
      { name: "input", type: "tensor", description: "Input tensor" }
    ],
    example: '["forward", {"layers": [{"type": "dense", "units": 2}]}, [0.5]]'
  }
};

// modules/tensor.ts
var TENSOR_OPERATORS = {
  // ============================================================================
  // Creation
  // ============================================================================
  "tensor/from": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 1,
    description: "Create tensor from array",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "data", type: "array", description: "Array of numbers (can be nested for multi-dimensional)" }
    ],
    example: '["tensor/from", [1.0, 2.0, 3.0]]'
  },
  "tensor/zeros": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 1,
    description: "Create tensor filled with zeros",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "shape", type: "number[]", description: "Shape of the tensor" }
    ],
    example: '["tensor/zeros", [3, 4]] // 3x4 tensor of zeros'
  },
  "tensor/ones": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 1,
    description: "Create tensor filled with ones",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "shape", type: "number[]", description: "Shape of the tensor" }
    ],
    example: '["tensor/ones", [3, 4]] // 3x4 tensor of ones'
  },
  "tensor/rand": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 1,
    description: "Create tensor with random values in [0, 1)",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "shape", type: "number[]", description: "Shape of the tensor" }
    ],
    example: '["tensor/rand", [16]] // Random 16-element vector'
  },
  "tensor/randn": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 1,
    description: "Create tensor with random values from standard normal distribution",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "shape", type: "number[]", description: "Shape of the tensor" }
    ],
    example: '["tensor/randn", [16]] // Random normal 16-element vector'
  },
  // ============================================================================
  // Shape & Indexing
  // ============================================================================
  "tensor/shape": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 1,
    description: "Get tensor shape as array",
    hasSideEffects: false,
    returnType: "number[]",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" }
    ],
    example: '["tensor/shape", [[1, 2], [3, 4]]] // => [2, 2]'
  },
  "tensor/get": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 2,
    maxArity: 2,
    description: "Get element at index",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" },
      { name: "index", type: "number | number[]", description: "Index (single for 1D, array for multi-D)" }
    ],
    example: '["tensor/get", [10, 20, 30, 40], 3] // Get 4th element'
  },
  "tensor/slice": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 3,
    maxArity: 3,
    description: "Get slice of tensor",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" },
      { name: "start", type: "number", description: "Start index" },
      { name: "end", type: "number", description: "End index (exclusive)" }
    ],
    example: '["tensor/slice", [10, 20, 30, 40], 0, 3] // First 3 elements'
  },
  "tensor/reshape": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 2,
    maxArity: 2,
    description: "Reshape tensor to new shape (total elements must match)",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" },
      { name: "shape", type: "number[]", description: "New shape" }
    ],
    example: '["tensor/reshape", [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16], [4, 4]] // Reshape to 4x4'
  },
  "tensor/flatten": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 1,
    description: "Flatten tensor to 1D",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" }
    ],
    example: '["tensor/flatten", [[1, 2], [3, 4]]]'
  },
  // ============================================================================
  // Math Operations
  // ============================================================================
  "tensor/add": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 2,
    maxArity: 2,
    description: "Element-wise addition",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "a", type: "tensor", description: "First tensor" },
      { name: "b", type: "tensor | number", description: "Second tensor or scalar" }
    ],
    example: '["tensor/add", [1, 2], [3, 4]]'
  },
  "tensor/sub": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 2,
    maxArity: 2,
    description: "Element-wise subtraction",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "a", type: "tensor", description: "First tensor" },
      { name: "b", type: "tensor | number", description: "Second tensor or scalar" }
    ],
    example: '["tensor/sub", [1, 2], [3, 4]]'
  },
  "tensor/mul": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 2,
    maxArity: 2,
    description: "Element-wise multiplication",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "a", type: "tensor", description: "First tensor" },
      { name: "b", type: "tensor | number", description: "Second tensor or scalar" }
    ],
    example: '["tensor/mul", [0.5, 0.7], 0.99] // Decay weights'
  },
  "tensor/div": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 2,
    maxArity: 2,
    description: "Element-wise division",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "a", type: "tensor", description: "First tensor" },
      { name: "b", type: "tensor | number", description: "Second tensor or scalar" }
    ],
    example: '["tensor/div", [10, 20], 2]'
  },
  "tensor/matmul": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 2,
    maxArity: 2,
    description: "Matrix multiplication",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "a", type: "tensor", description: "First tensor (NxM)" },
      { name: "b", type: "tensor", description: "Second tensor (MxK)" }
    ],
    example: '["tensor/matmul", [[1, 2], [3, 4]], [[5, 6], [7, 8]]]'
  },
  "tensor/dot": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 2,
    maxArity: 2,
    description: "Dot product of two 1D tensors",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "a", type: "tensor", description: "First vector" },
      { name: "b", type: "tensor", description: "Second vector" }
    ],
    example: '["tensor/dot", [1, 2], [3, 4]]'
  },
  // ============================================================================
  // Reductions
  // ============================================================================
  "tensor/sum": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 2,
    description: "Sum of tensor elements",
    hasSideEffects: false,
    returnType: "number | tensor",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" },
      { name: "dim", type: "number", description: "Dimension to reduce", optional: true }
    ],
    example: '["tensor/sum", [1, 0, 1]] // Total reward'
  },
  "tensor/mean": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 2,
    description: "Mean of tensor elements",
    hasSideEffects: false,
    returnType: "number | tensor",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" },
      { name: "dim", type: "number", description: "Dimension to reduce", optional: true }
    ],
    example: '["tensor/mean", [0.2, 0.4, 0.6]]'
  },
  "tensor/max": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 2,
    description: "Maximum value in tensor",
    hasSideEffects: false,
    returnType: "number | tensor",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" },
      { name: "dim", type: "number", description: "Dimension to reduce", optional: true }
    ],
    example: '["tensor/max", [0.1, 0.9, 0.3]]'
  },
  "tensor/min": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 2,
    description: "Minimum value in tensor",
    hasSideEffects: false,
    returnType: "number | tensor",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" },
      { name: "dim", type: "number", description: "Dimension to reduce", optional: true }
    ],
    example: '["tensor/min", [3, 1, 2]]'
  },
  "tensor/argmax": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 2,
    description: "Index of maximum value",
    hasSideEffects: false,
    returnType: "number | tensor",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" },
      { name: "dim", type: "number", description: "Dimension to reduce", optional: true }
    ],
    example: '["tensor/argmax", [0.1, 0.9, 0.3]] // Best action index'
  },
  "tensor/norm": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 2,
    description: "L2 norm of tensor",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" },
      { name: "p", type: "number", description: "Norm order (default 2)", optional: true, defaultValue: 2 }
    ],
    example: '["tensor/norm", [3, 4]]'
  },
  // ============================================================================
  // Range Validation (for contracts)
  // ============================================================================
  "tensor/allInRange": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 2,
    maxArity: 2,
    description: "Check if all elements are within range [min, max]",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" },
      { name: "range", type: "[number, number]", description: "Range as [min, max]" }
    ],
    example: '["tensor/allInRange", [0.5, -0.2], [-1.0, 1.0]]'
  },
  "tensor/outOfRangeIndices": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 2,
    maxArity: 2,
    description: "Get indices of elements outside range",
    hasSideEffects: false,
    returnType: "number[]",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" },
      { name: "range", type: "[number, number]", description: "Range as [min, max]" }
    ],
    example: '["tensor/outOfRangeIndices", [0.5, 1.5], [-1.0, 1.0]]'
  },
  "tensor/clamp": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 3,
    maxArity: 3,
    description: "Clamp all elements to range [min, max]",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" },
      { name: "min", type: "number", description: "Minimum value" },
      { name: "max", type: "number", description: "Maximum value" }
    ],
    example: '["tensor/clamp", [5, -15, 3], -10.0, 10.0]'
  },
  "tensor/clampPerDim": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 2,
    maxArity: 2,
    description: "Clamp each dimension to its specified range",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" },
      { name: "ranges", type: "object", description: 'Per-dimension ranges { "0": {min, max}, ... }' }
    ],
    example: '["tensor/clampPerDim", [5, -15], {"0": {"min": 0, "max": 10}, "1": {"min": -10, "max": 10}}]'
  },
  "tensor/outOfRangeDims": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 2,
    maxArity: 2,
    description: "Get dimensions that exceed their specified ranges",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" },
      { name: "ranges", type: "object", description: 'Per-dimension ranges { "0": {min, max}, ... }' }
    ],
    example: '["tensor/outOfRangeDims", [5, -15], {"0": {"min": 0, "max": 10}, "1": {"min": -10, "max": 10}}]'
  },
  // ============================================================================
  // Conversion
  // ============================================================================
  "tensor/toArray": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 1,
    description: "Convert tensor to nested array",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" }
    ],
    example: '["tensor/toArray", [[1, 2], [3, 4]]]'
  },
  "tensor/toList": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 1,
    description: "Convert 1D tensor to flat array",
    hasSideEffects: false,
    returnType: "number[]",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor (must be 1D)" }
    ],
    example: '["tensor/toList", [1, 2, 3]]'
  },
  // ============================================================================
  // Concatenation / stacking / shape manipulation
  // ============================================================================
  "tensor/cat": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 2,
    maxArity: 2,
    description: "Concatenate two tensors along the last dimension",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "a", type: "tensor", description: "First tensor" },
      { name: "b", type: "tensor", description: "Second tensor" }
    ],
    example: '["tensor/cat", [1, 2], [3, 4]]'
  },
  "tensor/stack": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 2,
    maxArity: 2,
    description: "Stack two tensors along a new leading dimension",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "a", type: "tensor", description: "First tensor" },
      { name: "b", type: "tensor", description: "Second tensor" }
    ],
    example: '["tensor/stack", [1, 2], [3, 4]]'
  },
  "tensor/transpose": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 3,
    description: "Transpose a tensor (optionally swapping two named dimensions)",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor to transpose" },
      { name: "dim0", type: "number", description: "First dimension to swap (optional)" },
      { name: "dim1", type: "number", description: "Second dimension to swap (optional)" }
    ],
    example: '["tensor/transpose", [[1, 2], [3, 4]], 0, 1]'
  },
  "tensor/unsqueeze": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 2,
    maxArity: 2,
    description: "Insert a dimension of size one at the given position",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" },
      { name: "dim", type: "number", description: "Position to insert the new dimension" }
    ],
    example: '["tensor/unsqueeze", [1, 2, 3], 0]'
  },
  "tensor/squeeze": {
    module: "tensor",
    category: "ml-tensor",
    minArity: 1,
    maxArity: 1,
    description: "Remove all dimensions of size one",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "tensor", type: "tensor", description: "The tensor" }
    ],
    example: '["tensor/squeeze", [[1, 2, 3]]]'
  }
};

// modules/train.ts
var TRAIN_OPERATORS = {
  // ============================================================================
  // Training Loop
  // ============================================================================
  "train/loop": {
    module: "train",
    category: "ml-effect",
    minArity: 3,
    maxArity: 3,
    description: "Execute training loop with constraints",
    hasSideEffects: true,
    returnType: "train/result",
    params: [
      { name: "module", type: "nn/module", description: "The neural network to train" },
      { name: "data", type: "array", description: "Training data (array of {input, target} or experiences)" },
      { name: "config", type: "train/config", description: "Training configuration with constraints" }
    ],
    example: '["train/loop", {"layers": [{"type": "dense", "units": 2}]}, [{"state": [0], "action": 1, "reward": 1}], {"epochs": 10}]'
  },
  "train/step": {
    module: "train",
    category: "ml-effect",
    minArity: 4,
    maxArity: 4,
    description: "Execute single training step (forward, loss, backward, update)",
    hasSideEffects: true,
    returnType: "train/stepResult",
    params: [
      { name: "module", type: "nn/module", description: "The neural network" },
      { name: "input", type: "tensor", description: "Input tensor" },
      { name: "target", type: "tensor", description: "Target tensor" },
      { name: "config", type: "train/config", description: "Training configuration" }
    ],
    example: '["train/step", {"layers": [{"type": "dense", "units": 2}]}, [0.1, 0.2], [1], {"lr": 0.01}]'
  },
  // ============================================================================
  // Validation
  // ============================================================================
  "train/validate": {
    module: "train",
    category: "ml-effect",
    minArity: 2,
    maxArity: 2,
    description: "Validate model on test cases, returns pass/fail metrics",
    hasSideEffects: false,
    returnType: "train/validationResult",
    params: [
      { name: "module", type: "nn/module", description: "The neural network" },
      { name: "testCases", type: "array", description: "Array of {input, expected, tolerance?}" }
    ],
    example: '["train/validate", {"layers": [{"type": "dense", "units": 2}]}, [{"input": [0], "expected": [1]}]]'
  },
  "train/checkRegression": {
    module: "train",
    category: "ml-effect",
    minArity: 3,
    maxArity: 3,
    description: "Check if new model regresses on required invariants",
    hasSideEffects: false,
    returnType: "train/regressionResult",
    params: [
      { name: "newModule", type: "nn/module", description: "Newly trained network" },
      { name: "oldModule", type: "nn/module", description: "Previous network" },
      { name: "invariants", type: "array", description: "Test cases that must not regress" }
    ],
    example: '["train/checkRegression", [0.1, 0.2], {"layers": [{"type": "dense", "units": 2}]}, [{"input": [0], "expected": [1]}]]'
  },
  // ============================================================================
  // Constraint Checking
  // ============================================================================
  "train/checkConstraints": {
    module: "train",
    category: "ml-effect",
    minArity: 2,
    maxArity: 2,
    description: "Check if network weights satisfy all constraints",
    hasSideEffects: false,
    returnType: "train/constraintResult",
    params: [
      { name: "module", type: "nn/module", description: "The neural network" },
      { name: "constraints", type: "train/constraints", description: "Constraint specification" }
    ],
    example: '["train/checkConstraints", [0.1, 0.2], {"maxMagnitude": 10}]'
  },
  "train/checkWeightMagnitude": {
    module: "train",
    category: "ml-effect",
    minArity: 2,
    maxArity: 2,
    description: "Check if all weights are within magnitude limit",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "module", type: "nn/module", description: "The neural network" },
      { name: "maxMagnitude", type: "number", description: "Maximum allowed weight magnitude" }
    ],
    example: '["train/checkWeightMagnitude", {"layers": [{"type": "dense", "units": 2}]}, 10.0]'
  },
  "train/checkForbiddenOutputs": {
    module: "train",
    category: "ml-effect",
    minArity: 3,
    maxArity: 3,
    description: "Check if model produces outputs in forbidden regions",
    hasSideEffects: false,
    returnType: "train/forbiddenResult",
    params: [
      { name: "module", type: "nn/module", description: "The neural network" },
      { name: "testInputs", type: "array", description: "Inputs to test" },
      { name: "forbiddenRegions", type: "array", description: "Forbidden output regions" }
    ],
    example: '["train/checkForbiddenOutputs", {"layers": [{"type": "dense", "units": 2}]}, [[0], [1]], {"0": {"min": 100, "max": 200}}]'
  },
  // ============================================================================
  // Gradient Operations
  // ============================================================================
  "train/clipGradients": {
    module: "train",
    category: "ml-effect",
    minArity: 2,
    maxArity: 2,
    description: "Clip gradients to max norm (modifies in place)",
    hasSideEffects: true,
    returnType: "number",
    params: [
      { name: "module", type: "nn/module", description: "The neural network" },
      { name: "maxNorm", type: "number", description: "Maximum gradient norm" }
    ],
    example: '["train/clipGradients", {"layers": [{"type": "dense", "units": 2}]}, 1.0]'
  },
  "train/getGradientNorm": {
    module: "train",
    category: "ml-effect",
    minArity: 1,
    maxArity: 1,
    description: "Get current gradient norm",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "module", type: "nn/module", description: "The neural network" }
    ],
    example: '["train/getGradientNorm", {"layers": [{"type": "dense", "units": 2}]}]'
  },
  // ============================================================================
  // Weight Operations
  // ============================================================================
  "train/clipWeights": {
    module: "train",
    category: "ml-effect",
    minArity: 2,
    maxArity: 2,
    description: "Clip weights to max magnitude (modifies in place)",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "module", type: "nn/module", description: "The neural network" },
      { name: "maxMagnitude", type: "number", description: "Maximum weight magnitude" }
    ],
    example: '["train/clipWeights", {"layers": [{"type": "dense", "units": 2}]}, 10.0]'
  },
  "train/getMaxWeightMagnitude": {
    module: "train",
    category: "ml-effect",
    minArity: 1,
    maxArity: 1,
    description: "Get maximum weight magnitude in network",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "module", type: "nn/module", description: "The neural network" }
    ],
    example: '["train/getMaxWeightMagnitude", {"layers": [{"type": "dense", "units": 2}]}]'
  },
  // ============================================================================
  // Loss Functions
  // ============================================================================
  "train/mse": {
    module: "train",
    category: "ml-effect",
    minArity: 2,
    maxArity: 2,
    description: "Mean squared error loss",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "predicted", type: "tensor", description: "Predicted values" },
      { name: "target", type: "tensor", description: "Target values" }
    ],
    example: '["train/mse", [0.5, 0.7], [1, 0]]'
  },
  "train/crossEntropy": {
    module: "train",
    category: "ml-effect",
    minArity: 2,
    maxArity: 2,
    description: "Cross-entropy loss for classification",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "logits", type: "tensor", description: "Raw model outputs (logits)" },
      { name: "labels", type: "tensor", description: "Target class labels" }
    ],
    example: '["train/crossEntropy", [2.0, 0.5], [1, 0]]'
  },
  "train/huber": {
    module: "train",
    category: "ml-effect",
    minArity: 2,
    maxArity: 3,
    description: "Huber loss (smooth L1, robust to outliers)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "predicted", type: "tensor", description: "Predicted values" },
      { name: "target", type: "tensor", description: "Target values" },
      { name: "delta", type: "number", description: "Threshold for quadratic vs linear", optional: true, defaultValue: 1 }
    ],
    example: '["train/huber", [0.5, 1.2], [1, 0], 1.0]'
  },
  // ============================================================================
  // Optimizers
  // ============================================================================
  "train/sgd": {
    module: "train",
    category: "ml-effect",
    minArity: 2,
    maxArity: 3,
    description: "Stochastic gradient descent optimizer step",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "module", type: "nn/module", description: "The neural network" },
      { name: "lr", type: "number", description: "Learning rate" },
      { name: "momentum", type: "number", description: "Momentum factor", optional: true, defaultValue: 0 }
    ],
    example: '["train/sgd", {"layers": [{"type": "dense", "units": 2}]}, 0.01, 0.9]'
  },
  "train/adam": {
    module: "train",
    category: "ml-effect",
    minArity: 2,
    maxArity: 4,
    description: "Adam optimizer step",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "module", type: "nn/module", description: "The neural network" },
      { name: "lr", type: "number", description: "Learning rate" },
      { name: "beta1", type: "number", description: "First moment decay", optional: true, defaultValue: 0.9 },
      { name: "beta2", type: "number", description: "Second moment decay", optional: true, defaultValue: 0.999 }
    ],
    example: '["train/adam", {"layers": [{"type": "dense", "units": 2}]}, 0.001]'
  },
  // ============================================================================
  // Experience Replay (for RL)
  // ============================================================================
  "train/sampleBatch": {
    module: "train",
    category: "ml-effect",
    minArity: 2,
    maxArity: 2,
    description: "Sample random batch from experience buffer",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "buffer", type: "array", description: "Experience buffer" },
      { name: "batchSize", type: "number", description: "Number of samples" }
    ],
    example: '["train/sampleBatch", [{"state": [0], "reward": 1}, {"state": [1], "reward": 0}], 2]'
  },
  "train/computeReturns": {
    module: "train",
    category: "ml-effect",
    minArity: 2,
    maxArity: 2,
    description: "Compute discounted returns from rewards",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "rewards", type: "array", description: "Array of rewards" },
      { name: "gamma", type: "number", description: "Discount factor" }
    ],
    example: '["train/computeReturns", [1, 0, 1], 0.99]'
  },
  "train/computeAdvantages": {
    module: "train",
    category: "ml-effect",
    minArity: 3,
    maxArity: 3,
    description: "Compute GAE advantages for policy gradient",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "rewards", type: "array", description: "Array of rewards" },
      { name: "values", type: "tensor", description: "Value estimates" },
      { name: "config", type: "object", description: "Config with gamma, lambda" }
    ],
    example: '["train/computeAdvantages", [1, 0, 1], [0.5, 0.4, 0.3], { "gamma": 0.99, "lambda": 0.95 }]'
  },
  // ============================================================================
  // ML lifecycle effects (bare aliases invoked by std ML behaviors)
  // ============================================================================
  "train": {
    module: "train",
    category: "ml-effect",
    minArity: 1,
    maxArity: 3,
    description: "Train a model over a dataset (ML effect)",
    hasSideEffects: true,
    returnType: "object",
    params: [
      { name: "model", type: "nn/module", description: "The neural network module" },
      { name: "data", type: "array", description: "Training data (optional)" },
      { name: "config", type: "object", description: "Training config (optional)" }
    ],
    example: '["train", {"layers": [{"type": "dense", "units": 2}]}, [[0, 1], [1, 0]], { "epochs": 100 }]'
  },
  "evaluate": {
    module: "train",
    category: "ml-effect",
    minArity: 1,
    maxArity: 2,
    description: "Evaluate a model against test cases (ML effect)",
    hasSideEffects: false,
    returnType: "object",
    params: [
      { name: "model", type: "nn/module", description: "The neural network module" },
      { name: "testCases", type: "array", description: "Test cases (optional)" }
    ],
    example: '["evaluate", {"layers": [{"type": "dense", "units": 2}]}, [{"input": [0], "expected": [1]}]]'
  },
  "checkpoint/save": {
    module: "train",
    category: "ml-effect",
    minArity: 2,
    maxArity: 2,
    description: "Save a model checkpoint (ML effect)",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "model", type: "nn/module", description: "The neural network module" },
      { name: "key", type: "string", description: "Checkpoint identifier" }
    ],
    example: '["checkpoint/save", {"layers": [{"type": "dense", "units": 2}]}, "ckpt-1"]'
  },
  "checkpoint/load": {
    module: "train",
    category: "ml-effect",
    minArity: 1,
    maxArity: 1,
    description: "Load a model checkpoint (ML effect)",
    hasSideEffects: false,
    returnType: "object",
    params: [
      { name: "key", type: "string", description: "Checkpoint identifier" }
    ],
    example: '["checkpoint/load", "ckpt-1"]'
  }
};

// modules/graph.ts
var GRAPH_OPERATORS = {
  // ============================================================================
  // Construction
  // ============================================================================
  "graph/from-entities": {
    module: "graph",
    category: "ml-graph",
    minArity: 1,
    maxArity: 1,
    description: "Build PyG graph from entity collections",
    hasSideEffects: false,
    returnType: "graph",
    params: [
      { name: "config", type: "object", description: "Graph construction config (nodes, edges, node-features, edge-features, directed)" }
    ],
    example: '["graph/from-entities", [{ "age": 30, "score": 0.5 }, { "age": 25, "score": 0.7 }]]'
  },
  "graph/from-adjacency": {
    module: "graph",
    category: "ml-graph",
    minArity: 2,
    maxArity: 2,
    description: "Build graph from adjacency matrix and features",
    hasSideEffects: false,
    returnType: "graph",
    params: [
      { name: "adjacency", type: "tensor", description: "Adjacency matrix" },
      { name: "features", type: "tensor", description: "Node feature matrix" }
    ],
    example: '["graph/from-adjacency", [[0, 1], [1, 0]], [[1.0], [2.0]]]'
  },
  "graph/from-edge-list": {
    module: "graph",
    category: "ml-graph",
    minArity: 2,
    maxArity: 2,
    description: "Build graph from edge list and features",
    hasSideEffects: false,
    returnType: "graph",
    params: [
      { name: "edges", type: "tensor", description: "Edge index tensor [2, num_edges]" },
      { name: "features", type: "tensor", description: "Node feature matrix" }
    ],
    example: '["graph/from-edge-list", [[0, 1]], [[1.0], [2.0]]]'
  },
  // ============================================================================
  // Transforms
  // ============================================================================
  "graph/add-self-loops": {
    module: "graph",
    category: "ml-graph",
    minArity: 1,
    maxArity: 1,
    description: "Add self-loop edges to graph",
    hasSideEffects: false,
    returnType: "graph",
    params: [
      { name: "graph", type: "graph", description: "Input graph" }
    ],
    example: '["graph/add-self-loops", ["graph/from-adjacency", [[0, 1], [1, 0]], [[1.0], [2.0]]]]'
  },
  "graph/to-undirected": {
    module: "graph",
    category: "ml-graph",
    minArity: 1,
    maxArity: 1,
    description: "Convert directed graph to undirected",
    hasSideEffects: false,
    returnType: "graph",
    params: [
      { name: "graph", type: "graph", description: "Input graph" }
    ],
    example: '["graph/to-undirected", ["graph/from-adjacency", [[0, 1], [1, 0]], [[1.0], [2.0]]]]'
  },
  "graph/subgraph": {
    module: "graph",
    category: "ml-graph",
    minArity: 2,
    maxArity: 2,
    description: "Extract subgraph by node mask",
    hasSideEffects: false,
    returnType: "graph",
    params: [
      { name: "graph", type: "graph", description: "Input graph" },
      { name: "mask", type: "tensor", description: "Boolean node mask" }
    ],
    example: '["graph/subgraph", ["graph/from-adjacency", [[0, 1], [1, 0]], [[1.0], [2.0]]], [true, false]]'
  },
  "graph/k-hop": {
    module: "graph",
    category: "ml-graph",
    minArity: 3,
    maxArity: 3,
    description: "K-hop neighborhood subgraph",
    hasSideEffects: false,
    returnType: "graph",
    params: [
      { name: "graph", type: "graph", description: "Input graph" },
      { name: "node", type: "number", description: "Center node index" },
      { name: "k", type: "number", description: "Number of hops" }
    ],
    example: '["graph/k-hop", ["graph/from-adjacency", [[0, 1], [1, 0]], [[1.0], [2.0]]], 0, 2]'
  },
  // ============================================================================
  // Accessors
  // ============================================================================
  "graph/node-features": {
    module: "graph",
    category: "ml-graph",
    minArity: 1,
    maxArity: 1,
    description: "Get node feature matrix",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "graph", type: "graph", description: "Input graph" }
    ],
    example: '["graph/node-features", ["graph/from-adjacency", [[0, 1], [1, 0]], [[1.0], [2.0]]]]'
  },
  "graph/edge-index": {
    module: "graph",
    category: "ml-graph",
    minArity: 1,
    maxArity: 1,
    description: "Get edge index tensor",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "graph", type: "graph", description: "Input graph" }
    ],
    example: '["graph/edge-index", ["graph/from-adjacency", [[0, 1], [1, 0]], [[1.0], [2.0]]]]'
  },
  "graph/edge-features": {
    module: "graph",
    category: "ml-graph",
    minArity: 1,
    maxArity: 1,
    description: "Get edge feature matrix",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "graph", type: "graph", description: "Input graph" }
    ],
    example: '["graph/edge-features", ["graph/from-adjacency", [[0, 1], [1, 0]], [[1.0], [2.0]]]]'
  },
  "graph/num-nodes": {
    module: "graph",
    category: "ml-graph",
    minArity: 1,
    maxArity: 1,
    description: "Get node count",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "graph", type: "graph", description: "Input graph" }
    ],
    example: '["graph/num-nodes", ["graph/from-adjacency", [[0, 1], [1, 0]], [[1.0], [2.0]]]]'
  },
  "graph/num-edges": {
    module: "graph",
    category: "ml-graph",
    minArity: 1,
    maxArity: 1,
    description: "Get edge count",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "graph", type: "graph", description: "Input graph" }
    ],
    example: '["graph/num-edges", ["graph/from-adjacency", [[0, 1], [1, 0]], [[1.0], [2.0]]]]'
  },
  "graph/degree": {
    module: "graph",
    category: "ml-graph",
    minArity: 1,
    maxArity: 1,
    description: "Get node degree tensor",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "graph", type: "graph", description: "Input graph" }
    ],
    example: '["graph/degree", ["graph/from-adjacency", [[0, 1], [1, 0]], [[1.0], [2.0]]]]'
  },
  // ============================================================================
  // Batching
  // ============================================================================
  "graph/batch": {
    module: "graph",
    category: "ml-graph",
    minArity: 1,
    maxArity: 1,
    description: "Batch multiple graphs into one",
    hasSideEffects: false,
    returnType: "graph",
    params: [
      { name: "graphs", type: "array", description: "List of graphs" }
    ],
    example: '["graph/batch", [["graph/from-adjacency", [[0, 1], [1, 0]], [[1.0], [2.0]]]]]'
  }
};

// modules/contract.ts
var CONTRACT_OPERATORS = {
  // ============================================================================
  // Validation
  // ============================================================================
  "contract/validate-input": {
    module: "contract",
    category: "ml-contract",
    minArity: 2,
    maxArity: 2,
    description: "Validate tensor against input contract. Returns { valid, violations } \u2014 guard on `valid`, or read `violations` for the detail.",
    hasSideEffects: false,
    returnType: "object",
    params: [
      { name: "tensor", type: "tensor", description: "Input tensor" },
      { name: "contract", type: "object", description: "Input contract spec" }
    ],
    example: '["contract/validate-input", [0.5, 0.7], {"ranges": {"0": {"min": 0, "max": 1}, "1": {"min": 0, "max": 1}}}]'
  },
  "contract/validate-output": {
    module: "contract",
    category: "ml-contract",
    minArity: 2,
    maxArity: 2,
    description: "Validate tensor against output contract. Returns { valid, violations } \u2014 guard on `valid`, or read `violations` for the detail.",
    hasSideEffects: false,
    returnType: "object",
    params: [
      { name: "tensor", type: "tensor", description: "Output tensor" },
      { name: "contract", type: "object", description: "Output contract spec" }
    ],
    example: '["contract/validate-output", [0.5, 0.7], {"ranges": {"0": {"min": 0, "max": 1}, "1": {"min": 0, "max": 1}}}]'
  },
  "contract/clamp-output": {
    module: "contract",
    category: "ml-contract",
    minArity: 2,
    maxArity: 2,
    description: "Clamp output tensor to contract ranges",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "tensor", type: "tensor", description: "Output tensor" },
      { name: "contract", type: "object", description: "Output contract spec" }
    ],
    example: '["contract/clamp-output", [0.5, 1.7], {"ranges": {"0": {"min": 0, "max": 1}, "1": {"min": 0, "max": 1}}}]'
  },
  "contract/violations": {
    module: "contract",
    category: "ml-contract",
    minArity: 2,
    maxArity: 2,
    description: "List contract violations",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "tensor", type: "tensor", description: "Tensor to check" },
      { name: "contract", type: "object", description: "Contract spec" }
    ],
    example: '["contract/violations", [0.5, 1.7], {"ranges": {"0": {"min": 0, "max": 1}, "1": {"min": 0, "max": 1}}}]'
  },
  // ============================================================================
  // Conversion
  // ============================================================================
  "contract/entity-to-tensor": {
    module: "contract",
    category: "ml-contract",
    minArity: 2,
    maxArity: 2,
    description: "Convert entity fields to tensor via contract",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "entity", type: "object", description: "Entity data" },
      { name: "contract", type: "object", description: "Input contract mapping fields to dims" }
    ],
    example: '["contract/entity-to-tensor", {"price": 10, "qty": 2}, {"fields": ["price", "qty"]}]'
  },
  "contract/tensor-to-payload": {
    module: "contract",
    category: "ml-contract",
    minArity: 2,
    maxArity: 2,
    description: "Convert output tensor to event payload via contract",
    hasSideEffects: false,
    returnType: "object",
    params: [
      { name: "tensor", type: "tensor", description: "Output tensor" },
      { name: "contract", type: "object", description: "Output contract mapping dims to fields" }
    ],
    example: '["contract/tensor-to-payload", [0.5, 0.7], {"fields": ["price", "qty"]}]'
  }
};

// modules/data.ts
var DATA_OPERATORS = {
  // ============================================================================
  // Dataset Creation
  // ============================================================================
  "data/dataset": {
    module: "data",
    category: "ml-data",
    minArity: 2,
    maxArity: 2,
    description: "Create dataset from entity collection and contracts",
    hasSideEffects: false,
    returnType: "object",
    params: [
      { name: "entities", type: "array", description: "Entity collection" },
      { name: "contracts", type: "object", description: "Input/output contract pair" }
    ],
    example: '["data/dataset", [{"x": 1, "y": 2}], {"input": {"fields": ["x"]}, "output": {"fields": ["y"]}}]'
  },
  "data/dataloader": {
    module: "data",
    category: "ml-data",
    minArity: 2,
    maxArity: 2,
    description: "Create batched dataloader from dataset",
    hasSideEffects: false,
    returnType: "object",
    params: [
      { name: "dataset", type: "object", description: "Dataset" },
      { name: "config", type: "object", description: "Config with batch_size, shuffle, etc." }
    ],
    example: '["data/dataloader", ["data/dataset", [[1, 2], [3, 4], [5, 6]], {}], { "batch-size": 2, "shuffle": true }]'
  },
  "data/split": {
    module: "data",
    category: "ml-data",
    minArity: 2,
    maxArity: 2,
    description: "Train/validation split. Config carries `trainRatio` (default 0.8); the remainder is the validation split.",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "dataset", type: "object", description: "Dataset" },
      { name: "config", type: "object", description: "Split config, e.g. { trainRatio: 0.8 }" }
    ],
    example: '["data/split", ["data/dataset", [1, 2, 3, 4, 5], {}], { "trainRatio": 0.8 }]'
  },
  // ============================================================================
  // Preprocessing
  // ============================================================================
  "data/normalize": {
    module: "data",
    category: "ml-data",
    minArity: 2,
    maxArity: 2,
    description: "Normalize features (mean/std or min/max)",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "tensor", type: "tensor", description: "Feature tensor" },
      { name: "config", type: "object", description: "Normalization config (method, per_dim, etc.)" }
    ],
    example: '["data/normalize", [[1, 2], [3, 4]], { "method": "standard" }]'
  },
  "data/augment": {
    module: "data",
    category: "ml-data",
    minArity: 2,
    maxArity: 2,
    description: "Apply data augmentation",
    hasSideEffects: false,
    returnType: "object",
    params: [
      { name: "dataset", type: "object", description: "Dataset" },
      { name: "config", type: "object", description: "Augmentation config" }
    ],
    example: '["data/augment", [[1.0, 2.0], [3.0, 4.0]], { "flip": true, "rotate": 15 }]'
  },
  // ============================================================================
  // Tokenization & Padding
  // ============================================================================
  "data/tokenize": {
    module: "data",
    category: "ml-data",
    minArity: 2,
    maxArity: 2,
    description: "Tokenize text fields",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "text", type: "string", description: "Text to tokenize" },
      { name: "config", type: "object", description: "Tokenizer config (method, vocab_size, max_length)" }
    ],
    example: '["data/tokenize", "hello world", { "method": "bpe", "max-length": 512 }]'
  },
  "data/pad": {
    module: "data",
    category: "ml-data",
    minArity: 2,
    maxArity: 2,
    description: "Pad sequences to uniform length",
    hasSideEffects: false,
    returnType: "tensor",
    params: [
      { name: "sequences", type: "array", description: "List of token sequences" },
      { name: "config", type: "object", description: "Padding config (max_length, pad_value)" }
    ],
    example: '["data/pad", [[1, 2, 3], [4, 5]], { "max-length": 4, "pad-value": 0 }]'
  }
};

// modules/prob.ts
var PROB_OPERATORS = {
  // ========================================
  // Distribution Sampling
  // ========================================
  "prob/seed": {
    module: "prob",
    category: "std-prob",
    minArity: 1,
    maxArity: 1,
    description: "Set seeded PRNG for deterministic probabilistic sampling",
    hasSideEffects: true,
    returnType: "void",
    params: [{ name: "n", type: "number", description: "Seed value (integer)" }],
    example: '["prob/seed", 42]'
  },
  "prob/flip": {
    module: "prob",
    category: "std-prob",
    minArity: 1,
    maxArity: 1,
    description: "Bernoulli trial: returns true with probability p",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "p", type: "number", description: "Probability of true (0 to 1)" }],
    example: '["prob/flip", 0.5] // => true or false with equal probability'
  },
  "prob/gaussian": {
    module: "prob",
    category: "std-prob",
    minArity: 2,
    maxArity: 2,
    description: "Sample from a Gaussian (normal) distribution",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "mu", type: "number", description: "Mean" },
      { name: "sigma", type: "number", description: "Standard deviation" }
    ],
    example: '["prob/gaussian", 0, 1] // => standard normal sample'
  },
  "prob/uniform": {
    module: "prob",
    category: "std-prob",
    minArity: 2,
    maxArity: 2,
    description: "Sample from a uniform distribution [lo, hi)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "lo", type: "number", description: "Lower bound (inclusive)" },
      { name: "hi", type: "number", description: "Upper bound (exclusive)" }
    ],
    example: '["prob/uniform", 0, 10] // => number in [0, 10)'
  },
  "prob/beta": {
    module: "prob",
    category: "std-prob",
    minArity: 2,
    maxArity: 2,
    description: "Sample from a Beta(alpha, beta) distribution",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "alpha", type: "number", description: "Alpha shape parameter (> 0)" },
      { name: "beta", type: "number", description: "Beta shape parameter (> 0)" }
    ],
    example: '["prob/beta", 2, 5] // => number in [0, 1], mean ~ 0.286'
  },
  "prob/categorical": {
    module: "prob",
    category: "std-prob",
    minArity: 2,
    maxArity: 2,
    description: "Weighted random selection from items",
    hasSideEffects: false,
    returnType: "any",
    params: [
      { name: "items", type: "array", description: "Array of items to choose from" },
      { name: "weights", type: "number[]", description: "Array of weights (same length as items)" }
    ],
    example: '["prob/categorical", ["a", "b", "c"], [1, 2, 1]] // => "b" most likely'
  },
  "prob/poisson": {
    module: "prob",
    category: "std-prob",
    minArity: 1,
    maxArity: 1,
    description: "Sample from a Poisson distribution",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "lambda", type: "number", description: "Rate parameter (> 0)" }],
    example: '["prob/poisson", 4] // => non-negative integer, mean ~ 4'
  },
  // ========================================
  // Inference
  // ========================================
  "prob/condition": {
    module: "prob",
    category: "std-prob",
    minArity: 1,
    maxArity: 1,
    description: "Mark current sample as rejected if predicate is false",
    hasSideEffects: true,
    returnType: "void",
    params: [{ name: "predicate", type: "boolean", description: "Condition that must hold" }],
    example: '["prob/condition", [">", 5, 0]]'
  },
  "prob/sample": {
    module: "prob",
    category: "std-prob",
    minArity: 2,
    maxArity: 2,
    description: "Evaluate an expression n times and collect results",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "n", type: "number", description: "Number of samples" },
      { name: "expr", type: "SExpr", description: "Expression to evaluate (lazy)" }
    ],
    example: '["prob/sample", 1000, ["prob/flip", 0.5]] // => array of booleans'
  },
  "prob/posterior": {
    module: "prob",
    category: "std-prob",
    minArity: 4,
    maxArity: 4,
    description: "Rejection sampling: returns accepted query values",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "model", type: "SExpr", description: "Model expression (lazy, may call set/condition)" },
      { name: "evidence", type: "SExpr", description: "Evidence expression (lazy, boolean)" },
      { name: "query", type: "SExpr", description: "Query expression (lazy, value to collect)" },
      { name: "n", type: "number", description: "Number of samples to attempt" }
    ],
    example: '["prob/posterior", ["prob/gaussian", 0, 1], [0.5], "mu", 5000]'
  },
  "prob/infer": {
    module: "prob",
    category: "std-prob",
    minArity: 4,
    maxArity: 4,
    description: "Like posterior but returns {mean, variance, samples, acceptRate}",
    hasSideEffects: false,
    returnType: "object",
    params: [
      { name: "model", type: "SExpr", description: "Model expression (lazy)" },
      { name: "evidence", type: "SExpr", description: "Evidence expression (lazy, boolean)" },
      { name: "query", type: "SExpr", description: "Query expression (lazy)" },
      { name: "n", type: "number", description: "Number of samples to attempt" }
    ],
    example: '["prob/infer", ["prob/gaussian", 0, 1], [0.5], "mu", 5000]'
  },
  // ========================================
  // Statistics
  // ========================================
  "prob/expected-value": {
    module: "prob",
    category: "std-prob",
    minArity: 1,
    maxArity: 1,
    description: "Mean of numeric samples",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "samples", type: "number[]", description: "Array of numeric samples" }],
    example: '["prob/expected-value", [2, 4, 6, 8]] // => 5'
  },
  "prob/variance": {
    module: "prob",
    category: "std-prob",
    minArity: 1,
    maxArity: 1,
    description: "Population variance of numeric samples",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "samples", type: "number[]", description: "Array of numeric samples" }],
    example: '["prob/variance", [2, 4, 4, 4, 5, 5, 7, 9]] // => 4'
  },
  "prob/histogram": {
    module: "prob",
    category: "std-prob",
    minArity: 2,
    maxArity: 2,
    description: "Bin numeric samples into a histogram",
    hasSideEffects: false,
    returnType: "object",
    params: [
      { name: "samples", type: "number[]", description: "Array of numeric samples" },
      { name: "bins", type: "number", description: "Number of bins" }
    ],
    example: '["prob/histogram", [1, 2, 3, 4, 5], 2] // => {binEdges, counts}'
  },
  "prob/percentile": {
    module: "prob",
    category: "std-prob",
    minArity: 2,
    maxArity: 2,
    description: "Get the p-th percentile (0-100) from samples",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "samples", type: "number[]", description: "Array of numeric samples" },
      { name: "p", type: "number", description: "Percentile (0 to 100)" }
    ],
    example: '["prob/percentile", [1, 2, 3, 4, 5], 50] // => 3'
  },
  "prob/credible-interval": {
    module: "prob",
    category: "std-prob",
    minArity: 2,
    maxArity: 2,
    description: "Compute symmetric credible interval from samples",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "samples", type: "number[]", description: "Array of numeric samples" },
      { name: "alpha", type: "number", description: "Significance level (e.g., 0.05 for 95% interval)" }
    ],
    example: '["prob/credible-interval", [1, 2, 3, 4, 5], 0.05] // => [lo, hi]'
  }
};

// modules/os.ts
var OS_OPERATORS = {
  "os/watch-files": {
    module: "os",
    category: "std-os",
    minArity: 1,
    maxArity: 2,
    description: "Register file system watcher. Emits OS_FILE_MODIFIED, OS_FILE_CREATED, OS_FILE_DELETED events with { path, name, dir, timestamp } payload.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "glob", type: "string", description: 'Glob pattern to watch (e.g., "**/*.orb")' },
      { name: "options", type: "object", description: "Optional config: { recursive, ignoreInitial, debounceMs }", optional: true }
    ],
    example: '["os/watch-files", "**/*.orb"]'
  },
  "os/watch-process": {
    module: "os",
    category: "std-os",
    minArity: 1,
    maxArity: 2,
    description: "Register process monitor. Emits OS_PROCESS_EXITED, OS_PROCESS_STARTED events with { pid, name, exitCode, signal, args, duration } payload.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "name", type: "string", description: 'Process name to monitor (e.g., "orbital")' },
      { name: "subcommand", type: "string", description: 'Optional subcommand filter (e.g., "validate")', optional: true }
    ],
    example: '["os/watch-process", "orbital", "validate"]'
  },
  "os/watch-port": {
    module: "os",
    category: "std-os",
    minArity: 1,
    maxArity: 2,
    description: "Register port monitor. Emits OS_PORT_OPENED, OS_PORT_CLOSED events with { port, protocol, pid, address } payload.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "port", type: "number", description: "Port number to monitor" },
      { name: "protocol", type: "string", description: 'Protocol: "tcp" or "udp" (default: "tcp")', optional: true }
    ],
    example: '["os/watch-port", 3000]'
  },
  "os/watch-http": {
    module: "os",
    category: "std-os",
    minArity: 1,
    maxArity: 2,
    description: "Register HTTP response interceptor. Emits OS_HTTP_RESPONSE events with { url, method, status, headers, body, duration } payload.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "urlPattern", type: "string", description: 'URL pattern to intercept (e.g., "https://api.example.com/*")' },
      { name: "method", type: "string", description: 'HTTP method filter (e.g., "POST")', optional: true }
    ],
    example: '["os/watch-http", "https://api.example.com/*", "POST"]'
  },
  "os/watch-cron": {
    module: "os",
    category: "std-os",
    minArity: 1,
    maxArity: 1,
    description: "Register cron schedule. Emits OS_CRON_FIRE events with { expression, timestamp } payload.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "expression", type: "string", description: 'Cron expression (e.g., "*/5 * * * *" for every 5 minutes)' }
    ],
    example: '["os/watch-cron", "0 */6 * * *"]'
  },
  "os/watch-signal": {
    module: "os",
    category: "std-os",
    minArity: 1,
    maxArity: 1,
    description: "Register OS signal handler. Emits OS_SIGNAL_USR1, OS_SIGNAL_HUP, etc. with { pid, timestamp } payload.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "signal", type: "string", description: 'Signal name: "SIGUSR1", "SIGUSR2", "SIGHUP", "SIGTERM"' }
    ],
    example: '["os/watch-signal", "SIGUSR1"]'
  },
  "os/watch-env": {
    module: "os",
    category: "std-os",
    minArity: 1,
    maxArity: 1,
    description: "Register environment variable watcher. Emits OS_ENV_CHANGED events with { variable, oldValue, newValue } payload.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "variable", type: "string", description: "Environment variable name to watch" }
    ],
    example: '["os/watch-env", "NODE_ENV"]'
  },
  "os/debounce": {
    module: "os",
    category: "std-os",
    minArity: 2,
    maxArity: 2,
    description: "Configure debounce interval for an OS event type. Prevents trigger storms during rapid changes.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "ms", type: "number", description: "Debounce interval in milliseconds" },
      { name: "eventType", type: "string", description: 'OS event type to debounce (e.g., "OS_FILE_MODIFIED")' }
    ],
    example: '["os/debounce", 500, "OS_FILE_MODIFIED"]'
  }
};

// modules/browser.ts
var BROWSER_OPERATORS = {
  "browser/open-file-picker": {
    module: "browser",
    category: "std-browser",
    minArity: 0,
    maxArity: 1,
    description: "Open the OS file picker (window.showOpenFilePicker / host dialog). Resolves with the selected file metadata list, or emits failure when the user cancels or the capability is denied. Async; use the trailing { emit } envelope for success/failure events.",
    hasSideEffects: true,
    returnType: "array",
    params: [
      {
        name: "options",
        type: {
          kind: "object",
          fields: {
            multiple: "string",
            accept: "string"
          },
          open: true
        },
        description: "Optional picker config: { multiple?: boolean, accept?: string, types?: Array<{description, accept}> }",
        optional: true
      }
    ],
    effect: {
      kind: "custom",
      produces: {
        kind: "array",
        of: {
          kind: "object",
          fields: {
            name: "string",
            size: "number",
            type: "string",
            lastModified: "number"
          }
        }
      }
    },
    example: '["browser/open-file-picker", { "multiple": false }, { "emit": { "success": "FILES_PICKED", "failure": "PICK_CANCELLED" } }]'
  },
  "browser/clipboard-read": {
    module: "browser",
    category: "std-browser",
    minArity: 0,
    maxArity: 0,
    description: "Read text from the system clipboard (navigator.clipboard.readText / host clipboard). Requires a user gesture and clipboard permission. Resolves with the clipboard text via the trailing { emit } envelope.",
    hasSideEffects: true,
    returnType: "string",
    effect: {
      kind: "custom",
      produces: "string"
    },
    example: '["browser/clipboard-read", { "emit": { "success": "CLIPBOARD_READ", "failure": "CLIPBOARD_DENIED" } }]'
  },
  "browser/clipboard-write": {
    module: "browser",
    category: "std-browser",
    minArity: 1,
    maxArity: 1,
    description: "Write text to the system clipboard (navigator.clipboard.writeText / host clipboard). Requires a user gesture. Emits success/failure via the trailing { emit } envelope.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "text", type: "string", description: "Text to write to the clipboard" }
    ],
    effect: {
      kind: "custom",
      produces: "string"
    },
    example: '["browser/clipboard-write", "https://example.com/invite/abc", { "emit": { "success": "COPIED", "failure": "COPY_DENIED" } }]'
  },
  "browser/push-subscribe": {
    module: "browser",
    category: "std-browser",
    minArity: 0,
    maxArity: 0,
    description: "Subscribe this browser to Web Push notifications: registers the shared push service worker, fetches the VAPID public key from the host (/api/push/vapid-public-key \u2014 no key material in the schema), and calls PushManager.subscribe. Requires notification permission (user gesture). Resolves with the flat subscription credentials { endpoint, p256dh, auth } via the trailing { emit } envelope \u2014 persist them on an owner-scoped entity row and pass them back to the push.send service as subscription: { endpoint, keys: { p256dh, auth } }. Emits failure when the Push API is unavailable, permission is denied, or the host has no VAPID key configured.",
    hasSideEffects: true,
    returnType: "object",
    effect: {
      kind: "custom",
      produces: {
        kind: "object",
        fields: {
          endpoint: "string",
          p256dh: "string",
          auth: "string"
        }
      }
    },
    example: '["browser/push-subscribe", { "emit": { "success": "PUSH_SUBSCRIBED", "failure": "PUSH_SUBSCRIBE_FAILED" } }]'
  },
  "browser/geolocation-current": {
    module: "browser",
    category: "std-browser",
    minArity: 0,
    maxArity: 1,
    description: "Read the current device position (navigator.geolocation.getCurrentPosition / host geolocation). Requires location permission. Resolves with { latitude, longitude, accuracy } via the trailing { emit } envelope.",
    hasSideEffects: true,
    returnType: "object",
    params: [
      {
        name: "options",
        type: {
          kind: "object",
          fields: {
            enableHighAccuracy: "string",
            timeout: "number",
            maximumAge: "number"
          },
          open: true
        },
        description: "Optional PositionOptions: { enableHighAccuracy?: boolean, timeout?: number, maximumAge?: number }",
        optional: true
      }
    ],
    effect: {
      kind: "custom",
      produces: {
        kind: "object",
        fields: {
          latitude: "number",
          longitude: "number",
          accuracy: "number"
        }
      }
    },
    example: '["browser/geolocation-current", { "enableHighAccuracy": true }, { "emit": { "success": "LOCATED", "failure": "LOCATION_DENIED" } }]'
  }
};

// modules/composition.ts
var COMPOSITION_OPERATORS = {
  "behavior/compose": {
    module: "composition",
    category: "std-composition",
    minArity: 1,
    maxArity: 1,
    description: "Compose N orbitals into one application schema. Wires events, picks layout, generates pages.",
    hasSideEffects: false,
    compileTime: true,
    returnType: "object",
    params: [
      {
        name: "config",
        type: "object",
        description: "ComposeBehaviorsInput: { appName, orbitals[], layoutStrategy?, eventWiring?, entityMappings? }"
      }
    ],
    example: '(behavior/compose { appName: "ShoppingApp" orbitals: [...] })'
  },
  "behavior/wire": {
    module: "composition",
    category: "std-composition",
    minArity: 2,
    maxArity: 2,
    description: "Apply cross-orbital event wiring. Adds external emits/listens to inline traits.",
    hasSideEffects: false,
    compileTime: true,
    returnType: "array",
    params: [
      {
        name: "orbitals",
        type: "array",
        description: "Array of OrbitalDefinition to wire"
      },
      {
        name: "wiring",
        type: "array",
        description: "Array of EventWiringEntry { from, event, to, triggers }"
      }
    ],
    example: "(behavior/wire orbitals wiring)"
  },
  "behavior/detect-layout": {
    module: "composition",
    category: "std-composition",
    minArity: 1,
    maxArity: 2,
    description: "Auto-detect layout from orbital count and wiring topology. Returns single, tabs, sidebar, dashboard, or wizard-flow.",
    hasSideEffects: false,
    compileTime: true,
    returnType: "string",
    params: [
      {
        name: "orbitals",
        type: "array",
        description: "Array of OrbitalDefinition"
      },
      {
        name: "wiring",
        type: "array",
        description: "Optional EventWiringEntry array",
        optional: true
      }
    ],
    example: "(behavior/detect-layout orbitals)"
  },
  "behavior/pipe": {
    module: "composition",
    category: "std-composition",
    minArity: 2,
    maxArity: null,
    description: "Left-to-right composition. Each step receives the previous result as its first arg.",
    hasSideEffects: false,
    compileTime: true,
    returnType: "any",
    params: [
      {
        name: "seed",
        type: "any",
        description: "Initial value to pipe through steps"
      },
      {
        name: "...steps",
        type: "function[]",
        description: "Functions, each receiving the previous result as first argument"
      }
    ],
    example: '(behavior/pipe orbitals (behavior/wire wiring) (behavior/compose { appName: "X" }))'
  }
};

// modules/vector.ts
var VEC_OPERATORS = {
  "vec/add": {
    module: "vec",
    category: "std-vec",
    minArity: 2,
    maxArity: 2,
    description: "Componentwise vector addition a+b",
    hasSideEffects: false,
    returnType: "vector",
    params: [
      { name: "a", type: "vector", description: "First vector {x,y[,z]}" },
      { name: "b", type: "vector", description: "Second vector {x,y[,z]}" }
    ],
    example: '["vec/add", {"x":1,"y":2}, {"x":3,"y":4}] // => {"x":4,"y":6}'
  },
  "vec/sub": {
    module: "vec",
    category: "std-vec",
    minArity: 2,
    maxArity: 2,
    description: "Componentwise vector subtraction a-b",
    hasSideEffects: false,
    returnType: "vector",
    params: [
      { name: "a", type: "vector", description: "First vector {x,y[,z]}" },
      { name: "b", type: "vector", description: "Second vector {x,y[,z]}" }
    ],
    example: '["vec/sub", {"x":5,"y":7}, {"x":1,"y":2}] // => {"x":4,"y":5}'
  },
  "vec/scale": {
    module: "vec",
    category: "std-vec",
    minArity: 2,
    maxArity: 2,
    description: "Componentwise scalar multiply a*k",
    hasSideEffects: false,
    returnType: "vector",
    params: [
      { name: "a", type: "vector", description: "Vector {x,y[,z]}" },
      { name: "k", type: "number", description: "Scalar factor" }
    ],
    example: '["vec/scale", {"x":2,"y":3}, 2] // => {"x":4,"y":6}'
  },
  "vec/dot": {
    module: "vec",
    category: "std-vec",
    minArity: 2,
    maxArity: 2,
    description: "Dot product ax\xB7bx+ay\xB7by(+az\xB7bz)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "a", type: "vector", description: "First vector {x,y[,z]}" },
      { name: "b", type: "vector", description: "Second vector {x,y[,z]}" }
    ],
    example: '["vec/dot", {"x":1,"y":2}, {"x":3,"y":4}] // => 11'
  },
  "vec/cross": {
    module: "vec",
    category: "std-vec",
    minArity: 2,
    maxArity: 2,
    description: "3D cross product \u2192 vector; 2D \u2192 scalar ax\xB7by-ay\xB7bx",
    hasSideEffects: false,
    returnType: "vector | number",
    params: [
      { name: "a", type: "vector", description: "First vector {x,y[,z]}" },
      { name: "b", type: "vector", description: "Second vector {x,y[,z]}" }
    ],
    example: '["vec/cross", {"x":1,"y":0}, {"x":0,"y":1}] // => 1'
  },
  "vec/length": {
    module: "vec",
    category: "std-vec",
    minArity: 1,
    maxArity: 1,
    description: "Vector magnitude sqrt(dot(a,a))",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "a", type: "vector", description: "Vector {x,y[,z]}" }],
    example: '["vec/length", {"x":3,"y":4}] // => 5'
  },
  "vec/length-sq": {
    module: "vec",
    category: "std-vec",
    minArity: 1,
    maxArity: 1,
    description: "Squared magnitude dot(a,a)",
    hasSideEffects: false,
    returnType: "number",
    params: [{ name: "a", type: "vector", description: "Vector {x,y[,z]}" }],
    example: '["vec/length-sq", {"x":3,"y":4}] // => 25'
  },
  "vec/normalize": {
    module: "vec",
    category: "std-vec",
    minArity: 1,
    maxArity: 1,
    description: "Unit vector a/len; zero-length \u2192 zero vector (same dim)",
    hasSideEffects: false,
    returnType: "vector",
    params: [{ name: "a", type: "vector", description: "Vector {x,y[,z]}" }],
    example: '["vec/normalize", {"x":3,"y":4}] // => {"x":0.6,"y":0.8}'
  },
  "vec/distance": {
    module: "vec",
    category: "std-vec",
    minArity: 2,
    maxArity: 2,
    description: "Euclidean distance length(sub(a,b))",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "a", type: "vector", description: "First vector {x,y[,z]}" },
      { name: "b", type: "vector", description: "Second vector {x,y[,z]}" }
    ],
    example: '["vec/distance", {"x":0,"y":0}, {"x":3,"y":4}] // => 5'
  },
  "vec/distance-sq": {
    module: "vec",
    category: "std-vec",
    minArity: 2,
    maxArity: 2,
    description: "Squared distance length-sq(sub(a,b))",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "a", type: "vector", description: "First vector {x,y[,z]}" },
      { name: "b", type: "vector", description: "Second vector {x,y[,z]}" }
    ],
    example: '["vec/distance-sq", {"x":0,"y":0}, {"x":3,"y":4}] // => 25'
  },
  "vec/lerp": {
    module: "vec",
    category: "std-vec",
    minArity: 3,
    maxArity: 3,
    description: "Componentwise linear interpolation a+(b-a)*t",
    hasSideEffects: false,
    returnType: "vector",
    params: [
      { name: "a", type: "vector", description: "Start vector {x,y[,z]}" },
      { name: "b", type: "vector", description: "End vector {x,y[,z]}" },
      { name: "t", type: "number", description: "Interpolation factor" }
    ],
    example: '["vec/lerp", {"x":0,"y":0}, {"x":10,"y":20}, 0.5] // => {"x":5,"y":10}'
  },
  "vec/angle": {
    module: "vec",
    category: "std-vec",
    minArity: 1,
    maxArity: 2,
    description: "Heading atan2(y,x) (1 arg) or angle between two vectors (2 args), radians",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "a", type: "vector", description: "Vector {x,y[,z]}" },
      { name: "b", type: "vector", description: "Optional second vector", optional: true }
    ],
    example: '["vec/angle", {"x":1,"y":0}, {"x":0,"y":1}] // => 1.5707963267948966'
  },
  "vec/rotate": {
    module: "vec",
    category: "std-vec",
    minArity: 2,
    maxArity: 2,
    description: "2D rotate by rad (z preserved): x'=x\xB7cos-y\xB7sin, y'=x\xB7sin+y\xB7cos",
    hasSideEffects: false,
    returnType: "vector",
    params: [
      { name: "a", type: "vector", description: "Vector {x,y[,z]}" },
      { name: "rad", type: "number", description: "Rotation angle in radians" }
    ],
    example: '["vec/rotate", {"x":1,"y":0}, 1.5707963267948966] // => {"x":0,"y":1}'
  },
  "vec/clamp-length": {
    module: "vec",
    category: "std-vec",
    minArity: 2,
    maxArity: 2,
    description: "Clamp magnitude to max (preserves direction)",
    hasSideEffects: false,
    returnType: "vector",
    params: [
      { name: "a", type: "vector", description: "Vector {x,y[,z]}" },
      { name: "max", type: "number", description: "Maximum magnitude" }
    ],
    example: '["vec/clamp-length", {"x":3,"y":4}, 2.5] // => {"x":1.5,"y":2}'
  }
};

// modules/geo.ts
var GEO_OPERATORS = {
  "geo/aabb-overlap": {
    module: "geo",
    category: "std-geo",
    minArity: 2,
    maxArity: 2,
    description: "Axis-aligned bounding box overlap test",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "a", type: "rect", description: "First rect {x,y,w,h}" },
      { name: "b", type: "rect", description: "Second rect {x,y,w,h}" }
    ],
    example: '["geo/aabb-overlap", {"x":0,"y":0,"w":10,"h":10}, {"x":5,"y":5,"w":10,"h":10}] // => true'
  },
  "geo/circle-overlap": {
    module: "geo",
    category: "std-geo",
    minArity: 2,
    maxArity: 2,
    description: "Circle overlap test: distance(centers) <= a.r+b.r",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "a", type: "circle", description: "First circle {x,y,r}" },
      { name: "b", type: "circle", description: "Second circle {x,y,r}" }
    ],
    example: '["geo/circle-overlap", {"x":0,"y":0,"r":3}, {"x":4,"y":0,"r":2}] // => true'
  },
  "geo/rect-circle-overlap": {
    module: "geo",
    category: "std-geo",
    minArity: 2,
    maxArity: 2,
    description: "Rect-circle overlap via closest point on rect to circle center",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "rect", type: "rect", description: "Rect {x,y,w,h}" },
      { name: "circle", type: "circle", description: "Circle {x,y,r}" }
    ],
    example: '["geo/rect-circle-overlap", {"x":0,"y":0,"w":10,"h":10}, {"x":12,"y":5,"r":3}] // => true'
  },
  "geo/point-in-rect": {
    module: "geo",
    category: "std-geo",
    minArity: 2,
    maxArity: 2,
    description: "Point containment in rect (inclusive bounds)",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "p", type: "point", description: "Point {x,y}" },
      { name: "rect", type: "rect", description: "Rect {x,y,w,h}" }
    ],
    example: '["geo/point-in-rect", {"x":5,"y":5}, {"x":0,"y":0,"w":10,"h":10}] // => true'
  },
  "geo/point-in-circle": {
    module: "geo",
    category: "std-geo",
    minArity: 2,
    maxArity: 2,
    description: "Point containment in circle (inclusive)",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "p", type: "point", description: "Point {x,y}" },
      { name: "circle", type: "circle", description: "Circle {x,y,r}" }
    ],
    example: '["geo/point-in-circle", {"x":1,"y":1}, {"x":0,"y":0,"r":2}] // => true'
  },
  "geo/reflect": {
    module: "geo",
    category: "std-geo",
    minArity: 2,
    maxArity: 2,
    description: "Reflect vector v across normal n (assumed normalized)",
    hasSideEffects: false,
    returnType: "vector",
    params: [
      { name: "v", type: "vector", description: "Incident vector {x,y[,z]}" },
      { name: "n", type: "vector", description: "Surface normal (normalized)" }
    ],
    example: '["geo/reflect", {"x":1,"y":-1}, {"x":0,"y":1}] // => {"x":1,"y":1}'
  },
  "geo/segment-intersect": {
    module: "geo",
    category: "std-geo",
    minArity: 2,
    maxArity: 2,
    description: "Intersection point of two segments, or null when parallel/none",
    hasSideEffects: false,
    returnType: "point | null",
    params: [
      { name: "a", type: "segment", description: "First segment {x1,y1,x2,y2}" },
      { name: "b", type: "segment", description: "Second segment {x1,y1,x2,y2}" }
    ],
    example: '["geo/segment-intersect", {"x1":0,"y1":0,"x2":4,"y2":4}, {"x1":0,"y1":4,"x2":4,"y2":0}] // => {"x":2,"y":2}'
  }
};

// modules/grid.ts
var GRID_OPERATORS = {
  "grid/to-world": {
    module: "grid",
    category: "std-grid",
    minArity: 2,
    maxArity: 2,
    description: "Cell to world-space center point",
    hasSideEffects: false,
    returnType: "point",
    params: [
      { name: "cell", type: "cell", description: "Grid cell {x,y}" },
      { name: "cellSize", type: "number", description: "Cell size in world units" }
    ],
    example: '["grid/to-world", {"x":2,"y":3}, 32] // => {"x":80,"y":112}'
  },
  "grid/from-world": {
    module: "grid",
    category: "std-grid",
    minArity: 2,
    maxArity: 2,
    description: "World point to grid cell (floor)",
    hasSideEffects: false,
    returnType: "cell",
    params: [
      { name: "point", type: "point", description: "World point {x,y}" },
      { name: "cellSize", type: "number", description: "Cell size in world units" }
    ],
    example: '["grid/from-world", {"x":80,"y":112}, 32] // => {"x":2,"y":3}'
  },
  "grid/iso-to-screen": {
    module: "grid",
    category: "std-grid",
    minArity: 3,
    maxArity: 3,
    description: "Isometric cell to screen point",
    hasSideEffects: false,
    returnType: "point",
    params: [
      { name: "cell", type: "cell", description: "Grid cell {x,y}" },
      { name: "tileW", type: "number", description: "Tile width" },
      { name: "tileH", type: "number", description: "Tile height" }
    ],
    example: '["grid/iso-to-screen", {"x":1,"y":0}, 64, 32] // => {"x":32,"y":16}'
  },
  "grid/screen-to-iso": {
    module: "grid",
    category: "std-grid",
    minArity: 3,
    maxArity: 3,
    description: "Screen point to isometric cell coordinates",
    hasSideEffects: false,
    returnType: "point",
    params: [
      { name: "point", type: "point", description: "Screen point {x,y}" },
      { name: "tileW", type: "number", description: "Tile width" },
      { name: "tileH", type: "number", description: "Tile height" }
    ],
    example: '["grid/screen-to-iso", {"x":32,"y":16}, 64, 32] // => {"x":1,"y":0}'
  },
  "grid/distance": {
    module: "grid",
    category: "std-grid",
    minArity: 2,
    maxArity: 2,
    description: "Chebyshev distance max(|dx|,|dy|)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "a", type: "cell", description: "First cell {x,y}" },
      { name: "b", type: "cell", description: "Second cell {x,y}" }
    ],
    example: '["grid/distance", {"x":0,"y":0}, {"x":3,"y":2}] // => 3'
  },
  "grid/manhattan-distance": {
    module: "grid",
    category: "std-grid",
    minArity: 2,
    maxArity: 2,
    description: "Manhattan distance |dx|+|dy|",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "a", type: "cell", description: "First cell {x,y}" },
      { name: "b", type: "cell", description: "Second cell {x,y}" }
    ],
    example: '["grid/manhattan-distance", {"x":0,"y":0}, {"x":3,"y":2}] // => 5'
  },
  "grid/neighbors": {
    module: "grid",
    category: "std-grid",
    minArity: 1,
    maxArity: 2,
    description: "Adjacent cells in order N,E,S,W (+NE,SE,SW,NW if diagonal)",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "cell", type: "cell", description: "Center cell {x,y}" },
      { name: "diagonal", type: "boolean", description: "Include diagonals", optional: true, defaultValue: false }
    ],
    example: '["grid/neighbors", {"x":1,"y":1}] // => [{"x":1,"y":0},{"x":2,"y":1},{"x":1,"y":2},{"x":0,"y":1}]'
  },
  "grid/cells-in-radius": {
    module: "grid",
    category: "std-grid",
    minArity: 2,
    maxArity: 2,
    description: "All cells within Euclidean radius r (row-major order)",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "cell", type: "cell", description: "Center cell {x,y}" },
      { name: "r", type: "number", description: "Radius" }
    ],
    example: '["grid/cells-in-radius", {"x":0,"y":0}, 1] // => [{"x":0,"y":-1},{"x":-1,"y":0},{"x":0,"y":0},{"x":1,"y":0},{"x":0,"y":1}]'
  },
  "grid/line": {
    module: "grid",
    category: "std-grid",
    minArity: 2,
    maxArity: 2,
    description: "Bresenham line cells from a to b, inclusive, in step order",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "a", type: "cell", description: "Start cell {x,y}" },
      { name: "b", type: "cell", description: "End cell {x,y}" }
    ],
    example: '["grid/line", {"x":0,"y":0}, {"x":2,"y":2}] // => [{"x":0,"y":0},{"x":1,"y":1},{"x":2,"y":2}]'
  },
  "grid/in-bounds": {
    module: "grid",
    category: "std-grid",
    minArity: 3,
    maxArity: 3,
    description: "Whether cell lies within a w\xD7h grid",
    hasSideEffects: false,
    returnType: "boolean",
    params: [
      { name: "cell", type: "cell", description: "Grid cell {x,y}" },
      { name: "w", type: "number", description: "Grid width" },
      { name: "h", type: "number", description: "Grid height" }
    ],
    example: '["grid/in-bounds", {"x":2,"y":3}, 5, 5] // => true'
  }
};

// modules/anim.ts
var ANIM_OPERATORS = {
  "anim/frame-at": {
    module: "anim",
    category: "std-anim",
    minArity: 3,
    maxArity: 4,
    description: "Frame index for elapsed time; loops by default, else clamps to last",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "elapsed", type: "number", description: "Elapsed time" },
      { name: "frameDur", type: "number", description: "Duration per frame" },
      { name: "frameCount", type: "number", description: "Total frame count" },
      { name: "loop", type: "boolean", description: "Loop the animation", optional: true, defaultValue: true }
    ],
    example: '["anim/frame-at", 0.35, 0.1, 3] // => 0'
  },
  "anim/sheet-rect": {
    module: "anim",
    category: "std-anim",
    minArity: 4,
    maxArity: 4,
    description: "Source rect {sx,sy,sw,sh} for a frame index on a sprite sheet",
    hasSideEffects: false,
    returnType: "object",
    params: [
      { name: "frameIndex", type: "number", description: "Zero-based frame index" },
      { name: "cols", type: "number", description: "Columns in the sheet" },
      { name: "frameW", type: "number", description: "Frame width" },
      { name: "frameH", type: "number", description: "Frame height" }
    ],
    example: '["anim/sheet-rect", 5, 4, 16, 16] // => {"sx":16,"sy":16,"sw":16,"sh":16}'
  },
  "anim/direction-from-delta": {
    module: "anim",
    category: "std-anim",
    minArity: 2,
    maxArity: 3,
    description: "Facing direction string from (dx,dy); 4-way (default) or 8-way",
    hasSideEffects: false,
    returnType: "string",
    params: [
      { name: "dx", type: "number", description: "X delta" },
      { name: "dy", type: "number", description: "Y delta" },
      { name: "ways", type: "number", description: "Number of directions (4 or 8)", optional: true, defaultValue: 4 }
    ],
    example: '["anim/direction-from-delta", 1, 0] // => "right"'
  }
};

// modules/ease.ts
var EASE_OPERATORS = {
  "ease/apply": {
    module: "ease",
    category: "std-ease",
    minArity: 2,
    maxArity: 2,
    description: "Apply an easing curve to t\u2208[0,1]: linear, in-quad, out-quad, in-out-quad, in-cubic, out-cubic, in-out-cubic, elastic-out, bounce-out (unknown \u2192 linear)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "curve", type: "string", description: "Easing curve name" },
      { name: "t", type: "number", description: "Progress (clamped to 0-1)" }
    ],
    example: '["ease/apply", "in-quad", 0.5] // => 0.25'
  },
  "ease/smoothstep": {
    module: "ease",
    category: "std-ease",
    minArity: 3,
    maxArity: 3,
    description: "Smoothstep: clamp((x-edge0)/(edge1-edge0),0,1) then t*t*(3-2*t)",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "edge0", type: "number", description: "Lower edge" },
      { name: "edge1", type: "number", description: "Upper edge" },
      { name: "x", type: "number", description: "Input value" }
    ],
    example: '["ease/smoothstep", 0, 1, 0.5] // => 0.5'
  }
};

// modules/noise.ts
var NOISE_OPERATORS = {
  "noise/perlin": {
    module: "noise",
    category: "std-noise",
    minArity: 2,
    maxArity: 3,
    description: "2D Perlin noise at (x,y) with optional seed; output [-1,1]",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "x", type: "number", description: "X coordinate" },
      { name: "y", type: "number", description: "Y coordinate" },
      { name: "seed", type: "number", description: "Optional integer seed (default 0)", optional: true }
    ],
    example: '["noise/perlin", 1.5, 2.5, 0] // => coherent value in [-1,1]'
  },
  "noise/simplex": {
    module: "noise",
    category: "std-noise",
    minArity: 2,
    maxArity: 3,
    description: "Value-coherent noise (perlin-derived) at (x,y) with optional seed; output [-1,1]",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "x", type: "number", description: "X coordinate" },
      { name: "y", type: "number", description: "Y coordinate" },
      { name: "seed", type: "number", description: "Optional integer seed (default 0)", optional: true }
    ],
    example: '["noise/simplex", 1.5, 2.5, 0] // => coherent value in [-1,1]'
  },
  "noise/fbm": {
    module: "noise",
    category: "std-noise",
    minArity: 3,
    maxArity: 4,
    description: "Fractal Brownian motion: summed perlin octaves (clamped 1..8); output [-1,1]",
    hasSideEffects: false,
    returnType: "number",
    params: [
      { name: "x", type: "number", description: "X coordinate" },
      { name: "y", type: "number", description: "Y coordinate" },
      { name: "octaves", type: "number", description: "Octave count (floored, clamped to 1..8)" },
      { name: "seed", type: "number", description: "Optional integer seed (default 0)", optional: true }
    ],
    example: '["noise/fbm", 1.5, 2.5, 4, 0] // => layered value in [-1,1]'
  }
};

// modules/path.ts
var PATH_OPERATORS = {
  "path/astar": {
    module: "path",
    category: "std-path",
    minArity: 5,
    maxArity: 6,
    description: "A* shortest path on a w\xD7h grid; returns cells start\u2192goal (incl. both) or [] if none",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "start", type: "vector", description: "Start cell {x,y}" },
      { name: "goal", type: "vector", description: "Goal cell {x,y}" },
      { name: "blocked", type: { kind: "array", of: "vector" }, description: "Blocked cells [{x,y}, ...]" },
      { name: "w", type: "number", description: "Grid width (x in [0,w))" },
      { name: "h", type: "number", description: "Grid height (y in [0,h))" },
      { name: "diagonal", type: "boolean", description: "Allow 8-direction moves (default false)", optional: true }
    ],
    example: '["path/astar", {"x":0,"y":0}, {"x":2,"y":0}, [], 3, 1] // => [{"x":0,"y":0},{"x":1,"y":0},{"x":2,"y":0}]'
  },
  "path/reachable": {
    module: "path",
    category: "std-path",
    minArity: 5,
    maxArity: 6,
    description: "BFS cells reachable within N moves from start (incl. start); sorted row-major",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "start", type: "vector", description: "Start cell {x,y}" },
      { name: "steps", type: "number", description: "Maximum number of moves" },
      { name: "blocked", type: { kind: "array", of: "vector" }, description: "Blocked cells [{x,y}, ...]" },
      { name: "w", type: "number", description: "Grid width (x in [0,w))" },
      { name: "h", type: "number", description: "Grid height (y in [0,h))" },
      { name: "diagonal", type: "boolean", description: "Allow 8-direction moves (default false)", optional: true }
    ],
    example: '["path/reachable", {"x":1,"y":1}, 1, [], 3, 3, false] // => 5 cross-shaped cells, row-major'
  }
};

// modules/llm.ts
var LLM_OPERATORS = {
  "llm/generate": {
    module: "llm",
    category: "std-llm",
    minArity: 1,
    maxArity: 2,
    description: "Generate text from an LLM. Returns the generated string.",
    hasSideEffects: true,
    returnType: "string",
    params: [
      { name: "prompt", type: "string", description: "Prompt text to send to the LLM" },
      { name: "options", type: { kind: "object", fields: { json: "boolean", maxTokens: "number", provider: "string", model: "string" }, open: true }, description: "Generation options", optional: true }
    ],
    example: '["llm/generate", "Summarize the user request"]'
  },
  "llm/call-tools": {
    module: "llm",
    category: "std-llm",
    minArity: 2,
    maxArity: 2,
    description: "Call the LLM with tool definitions. Returns the assistant response with optional tool calls and token usage.",
    hasSideEffects: true,
    returnType: "LlmCallToolsResult",
    params: [
      { name: "messages", type: { kind: "array", of: "LlmMessage" }, description: "Conversation messages" },
      { name: "tools", type: { kind: "array", of: "LlmToolDef" }, description: "Tool definitions available to the LLM" }
    ],
    example: '["llm/call-tools", [{"role": "user", "content": "What time is it?"}], [{"name": "getTime", "description": "Get current time", "parameters": {}}]]'
  },
  "llm/embed": {
    module: "llm",
    category: "std-llm",
    minArity: 1,
    maxArity: 1,
    description: "Generate embeddings for an array of texts. Returns a 2D array of floats.",
    hasSideEffects: true,
    returnType: "array",
    params: [
      { name: "texts", type: { kind: "array", of: "string" }, description: "Texts to embed" }
    ],
    example: '["llm/embed", ["hello", "world"]]'
  },
  "llm/token-count": {
    module: "llm",
    category: "std-llm",
    minArity: 0,
    maxArity: 0,
    description: "Get the current token count in the context window.",
    hasSideEffects: false,
    returnType: "number",
    params: [],
    example: '["llm/token-count"] // => 12450'
  },
  "llm/switch": {
    module: "llm",
    category: "std-llm",
    minArity: 1,
    maxArity: 2,
    description: "Switch the active LLM provider and optionally the model.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "provider", type: "string", description: 'Provider name (e.g. "deepseek", "openai")' },
      { name: "model", type: "string", description: "Model name override", optional: true }
    ],
    example: '["llm/switch", "openai", "gpt-4o"]'
  },
  "llm/compact": {
    module: "llm",
    category: "std-llm",
    minArity: 0,
    maxArity: 1,
    description: "Compact the context window. Returns before/after token counts.",
    hasSideEffects: true,
    returnType: "object",
    params: [
      { name: "strategy", type: { kind: "union", of: [{ kind: "literal", value: "hybrid" }, { kind: "literal", value: "summarize" }, { kind: "literal", value: "truncate" }, { kind: "literal", value: "extract" }] }, description: "Compaction strategy", optional: true }
    ],
    example: '["llm/compact", "summarize"]'
  }
};

// modules/workspace.ts
var WORKSPACE_OPERATORS = {
  "workspace/read-orbital": {
    module: "workspace",
    category: "std-workspace",
    minArity: 1,
    maxArity: 1,
    description: "Read an orbital definition from the workspace by name.",
    hasSideEffects: false,
    returnType: "object",
    params: [{ name: "name", type: "string", description: "Orbital name" }],
    example: '["workspace/read-orbital", "ProductOrbital"]'
  },
  "workspace/write-orbital": {
    module: "workspace",
    category: "std-workspace",
    minArity: 2,
    maxArity: 2,
    description: "Write an orbital definition to the workspace.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "name", type: "string", description: "Orbital name" },
      { name: "def", type: { kind: "entityRef" }, description: "Orbital definition to write" }
    ],
    example: '["workspace/write-orbital", "ProductOrbital", {"name": "ProductOrbital"}]'
  },
  "workspace/read-file": {
    module: "workspace",
    category: "std-workspace",
    minArity: 1,
    maxArity: 1,
    description: "Read a file from the workspace.",
    hasSideEffects: false,
    returnType: "string",
    params: [{ name: "path", type: "string", description: "File path relative to workspace root" }],
    example: '["workspace/read-file", ".almadar/sessions/Coordinator/messages.json"]'
  },
  "workspace/write-file": {
    module: "workspace",
    category: "std-workspace",
    minArity: 2,
    maxArity: 2,
    description: "Write a file to the workspace.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "path", type: "string", description: "File path relative to workspace root" },
      { name: "content", type: "string", description: "File content" }
    ],
    example: '["workspace/write-file", "orbitals/Product.orb", {"orbitals": []}]'
  },
  "workspace/exists": {
    module: "workspace",
    category: "std-workspace",
    minArity: 1,
    maxArity: 1,
    description: "Check if a file exists in the workspace.",
    hasSideEffects: false,
    returnType: "boolean",
    params: [{ name: "path", type: "string", description: "File path" }],
    example: '["workspace/exists", "orbitals/Product.orb"]'
  },
  "workspace/list-orbitals": {
    module: "workspace",
    category: "std-workspace",
    minArity: 0,
    maxArity: 0,
    description: "List all orbital names in the workspace.",
    hasSideEffects: false,
    returnType: "array",
    params: [],
    example: '["workspace/list-orbitals"] // => ["ProductOrbital", "CartOrbital"]'
  },
  "workspace/read-schema": {
    module: "workspace",
    category: "std-workspace",
    minArity: 0,
    maxArity: 0,
    description: "Read the composed schema.orb from the workspace.",
    hasSideEffects: false,
    returnType: "object",
    params: [],
    example: '["workspace/read-schema"]'
  },
  "workspace/write-schema": {
    module: "workspace",
    category: "std-workspace",
    minArity: 1,
    maxArity: 1,
    description: "Write the composed schema.orb to the workspace.",
    hasSideEffects: true,
    returnType: "void",
    params: [{ name: "schema", type: { kind: "entityRef" }, description: "Composed schema" }],
    example: '["workspace/write-schema", {"orbitals": [{"name": "ProductOrbital"}]}]'
  },
  "workspace/read-plan": {
    module: "workspace",
    category: "std-workspace",
    minArity: 0,
    maxArity: 0,
    description: "Read the plan snapshot from the workspace.",
    hasSideEffects: false,
    returnType: "object",
    params: [],
    example: '["workspace/read-plan"]'
  },
  "workspace/write-plan": {
    module: "workspace",
    category: "std-workspace",
    minArity: 1,
    maxArity: 1,
    description: "Write the plan snapshot to the workspace.",
    hasSideEffects: true,
    returnType: "void",
    params: [{ name: "snapshot", type: { kind: "object", fields: { id: "string", status: "string" } }, description: "Plan snapshot" }],
    example: '["workspace/write-plan", ["create entity", "add list page"]]'
  },
  "workspace/archive-orbital": {
    module: "workspace",
    category: "std-workspace",
    minArity: 1,
    maxArity: 1,
    description: "Archive (remove from active set) an orbital from the workspace.",
    hasSideEffects: true,
    returnType: "void",
    params: [{ name: "name", type: "string", description: "Orbital name to archive" }],
    example: '["workspace/archive-orbital", "OldFeatureOrbital"]'
  }
};

// modules/session.ts
var SESSION_OPERATORS = {
  "session/read-spec": {
    module: "session",
    category: "std-session",
    minArity: 1,
    maxArity: 1,
    description: "Read the per-orbital spec.json from the session store.",
    hasSideEffects: false,
    returnType: "object",
    params: [{ name: "orbitalName", type: "string", description: "Orbital name" }],
    example: '["session/read-spec", "ProductOrbital"]'
  },
  "session/write-spec": {
    module: "session",
    category: "std-session",
    minArity: 2,
    maxArity: 2,
    description: "Write the per-orbital spec.json to the session store.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "orbitalName", type: "string", description: "Orbital name" },
      { name: "spec", type: { kind: "object", fields: { orbitalName: "string", organism: "string", method: "string" } }, description: "Orbital spec" }
    ],
    example: '["session/write-spec", "ProductOrbital", {"goal": "manage products"}]'
  },
  "session/read-memory": {
    module: "session",
    category: "std-session",
    minArity: 1,
    maxArity: 1,
    description: "Read the per-orbital memory from the session store.",
    hasSideEffects: false,
    returnType: "object",
    params: [{ name: "orbitalName", type: "string", description: "Orbital name" }],
    example: '["session/read-memory", "ProductOrbital"]'
  },
  "session/write-memory": {
    module: "session",
    category: "std-session",
    minArity: 2,
    maxArity: 2,
    description: "Write per-orbital memory to the session store.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "orbitalName", type: "string", description: "Orbital name" },
      { name: "memory", type: { kind: "object", fields: { entityName: "string", notes: { kind: "array", of: "string" } }, open: true }, description: "Orbital memory" }
    ],
    example: '["session/write-memory", "ProductOrbital", {"notes": ["initial draft"]}]'
  },
  "session/read-history": {
    module: "session",
    category: "std-session",
    minArity: 1,
    maxArity: 1,
    description: "Read the per-orbital conversation history.",
    hasSideEffects: false,
    returnType: "array",
    params: [{ name: "orbitalName", type: "string", description: "Orbital name" }],
    example: '["session/read-history", "ProductOrbital"]'
  },
  "session/append-history": {
    module: "session",
    category: "std-session",
    minArity: 2,
    maxArity: 2,
    description: "Append an entry to the per-orbital conversation history.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "orbitalName", type: "string", description: "Orbital name" },
      { name: "entry", type: { kind: "object", fields: { role: "string", content: "string" } }, description: "History entry" }
    ],
    example: '["session/append-history", "ProductOrbital", { "role": "user", "content": "Add a search bar" }]'
  },
  "session/read-errors": {
    module: "session",
    category: "std-session",
    minArity: 1,
    maxArity: 1,
    description: "Read validation errors for an orbital.",
    hasSideEffects: false,
    returnType: "array",
    params: [{ name: "orbitalName", type: "string", description: "Orbital name" }],
    example: '["session/read-errors", "ProductOrbital"]'
  },
  "session/write-errors": {
    module: "session",
    category: "std-session",
    minArity: 2,
    maxArity: 2,
    description: "Write validation errors for an orbital.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "orbitalName", type: "string", description: "Orbital name" },
      { name: "errors", type: { kind: "array", of: "ValidationError" }, description: "Validation errors" }
    ],
    example: '["session/write-errors", "ProductOrbital", []]'
  },
  "session/read-analysis": {
    module: "session",
    category: "std-session",
    minArity: 0,
    maxArity: 0,
    description: "Read the pre-computed analysis result.",
    hasSideEffects: false,
    returnType: "object",
    params: [],
    example: '["session/read-analysis"]'
  }
};

// modules/memory.ts
var MEMORY_OPERATORS = {
  "memory/recall": {
    module: "memory",
    category: "std-memory",
    minArity: 1,
    maxArity: 2,
    description: "Search memories by semantic query. Returns matching memory records.",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "query", type: "string", description: "Semantic search query" },
      { name: "limit", type: "number", description: "Max results to return", optional: true }
    ],
    example: '["memory/recall", "user prefers dark mode"]'
  },
  "memory/store": {
    module: "memory",
    category: "std-memory",
    minArity: 2,
    maxArity: 3,
    description: "Store a new memory. Returns the new memory ID.",
    hasSideEffects: true,
    returnType: "string",
    params: [
      { name: "content", type: "string", description: "Memory content to store" },
      { name: "category", type: "string", description: "Category: preference, correction, pattern-affinity, entity-template, error-resolution" },
      { name: "scope", type: "string", description: "Scope: global or project", optional: true }
    ],
    example: '["memory/store", "user prefers PascalCase", "preference"]'
  },
  "memory/list": {
    module: "memory",
    category: "std-memory",
    minArity: 0,
    maxArity: 1,
    description: "List all memories, optionally filtered by category.",
    hasSideEffects: false,
    returnType: "array",
    params: [
      { name: "category", type: "string", description: "Memory category filter", optional: true }
    ],
    example: '["memory/list", "preference"]'
  }
};

// modules/trace.ts
var TRACE_OPERATORS = {
  "trace/emit": {
    module: "trace",
    category: "std-trace",
    minArity: 1,
    maxArity: 2,
    description: "Emit a structured trace event for observability (115 call sites in rabit today).",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "event", type: "string", description: "Event type name" },
      { name: "payload", type: { kind: "object", fields: {}, open: true }, description: "Event payload", optional: true }
    ],
    example: '["trace/emit", "coordinator_state_changed", {"phase": "planning"}]'
  },
  "trace/log": {
    module: "trace",
    category: "std-trace",
    minArity: 2,
    maxArity: 2,
    description: "Log a message at the given level.",
    hasSideEffects: true,
    returnType: "void",
    params: [
      { name: "level", type: { kind: "union", of: [{ kind: "literal", value: "info" }, { kind: "literal", value: "warn" }, { kind: "literal", value: "error" }, { kind: "literal", value: "debug" }] }, description: "Log level" },
      { name: "message", type: "string", description: "Log message" }
    ],
    example: '["trace/log", "info", "coordinator started"]'
  }
};

// modules/behavior.ts
var BEHAVIOR_OPERATORS = {
  "compose/compose-all": {
    module: "behavior",
    category: "std-behavior",
    minArity: 0,
    maxArity: 0,
    description: "Compose all orbitals in the workspace into a single schema. Backed by composeBehaviors.",
    hasSideEffects: true,
    returnType: "ComposeAllResult",
    params: [],
    example: '["compose/compose-all"]'
  },
  "compose/compose-children": {
    module: "behavior",
    category: "std-behavior",
    minArity: 1,
    maxArity: 1,
    description: "Compose child orbitals under a parent for recursive builds.",
    hasSideEffects: true,
    returnType: "ComposeChildrenResult",
    params: [{ name: "parentName", type: "string", description: "Parent orbital name" }],
    example: '["compose/compose-children", "GameScreenOrbital"]'
  },
  "behavior/instantiate": {
    module: "behavior",
    category: "std-behavior",
    minArity: 2,
    maxArity: 3,
    description: "Instantiate a behavior from the registry at runtime (meta-programming). Equivalent to compile-time `uses` but dynamic \u2014 the behavior name is a runtime value. Applies the same override surface (linkedEntity, events, config, fields).",
    hasSideEffects: true,
    returnType: "BuilderResult",
    params: [
      { name: "behavior", type: "string", description: 'Behavior/organism name (e.g. "std-ecommerce")' },
      { name: "orbitalName", type: "string", description: "Canonical orbital name to instantiate" },
      { name: "overrides", type: { kind: "object", fields: { linkedEntity: "string", method: "string" }, open: true }, description: "Override surface: linkedEntity, events, config, fields", optional: true }
    ],
    example: '["behavior/instantiate", "std-ecommerce", "ProductOrbital", { "linkedEntity": "Product" }]'
  },
  "behavior/call": {
    module: "behavior",
    category: "std-behavior",
    minArity: 2,
    maxArity: 3,
    description: "Call a behavior service action. Returns the ServiceCallResult union \u2014 resolves to the specific member when the (behavior, action) pair is a literal.",
    hasSideEffects: true,
    returnType: "ServiceCallResult",
    params: [
      { name: "behavior", type: "string", description: "Service/behavior name" },
      { name: "action", type: "string", description: "Action name" },
      { name: "args", type: { kind: "object", fields: {}, open: true }, description: "Action arguments", optional: true }
    ],
    example: '["behavior/call", "validator", "validate-composed"]'
  },
  "lolo/emit-body": {
    module: "behavior",
    category: "std-behavior",
    minArity: 3,
    maxArity: 3,
    description: "Emit a .lolo orbital body from the LLM (free-lolo path). Returns the emitted lolo source.",
    hasSideEffects: true,
    returnType: "LoloEmitResult",
    params: [
      { name: "name", type: "string", description: "Orbital name" },
      { name: "palette", type: { kind: "object", fields: { topics: { kind: "array", of: "string" }, hints: { kind: "array", of: "string" } } }, description: "Topic-tree palette + primitive hints" },
      { name: "deltaPrompt", type: "string", description: "Per-orbital instruction prompt" }
    ],
    example: '["lolo/emit-body", "PlayerOrbital", { "topics": ["game/2d"] }, "player ship with movement"]'
  },
  "validate/validate": {
    module: "behavior",
    category: "std-behavior",
    minArity: 1,
    maxArity: 2,
    description: "Validate an orbital or the composed schema using orb validate.",
    hasSideEffects: true,
    returnType: "ValidateResult",
    params: [
      { name: "target", type: { kind: "union", of: [{ kind: "literal", value: "orbital" }, { kind: "literal", value: "composed" }] }, description: "Validation target" },
      { name: "name", type: "string", description: 'Orbital name (required when target is "orbital")', optional: true }
    ],
    example: '["validate/validate", "composed"]'
  }
};

// modules/integration.ts
var INTEGRATION_OPERATORS = {
  "integration/http": {
    module: "integration",
    category: "std-integration",
    minArity: 3,
    maxArity: 4,
    description: "HTTP request with caller-declared response schema. Validates the response against the schema before returning.",
    hasSideEffects: true,
    returnType: "object",
    params: [
      { name: "method", type: "string", description: "HTTP method (GET, POST, ...)" },
      { name: "url", type: "string", description: "Request URL" },
      { name: "responseSchema", type: { kind: "object", fields: {}, open: true }, description: "JSON Schema the response must satisfy" },
      { name: "body", type: { kind: "object", fields: {}, open: true }, description: "Request body (for POST/PUT)", optional: true }
    ],
    example: '["integration/http", "GET", "https://api.github.com/repos/foo/bar", { "name": "string", "stars": "number" }]'
  },
  "integration/github-get-repo": {
    module: "integration",
    category: "std-integration",
    minArity: 2,
    maxArity: 2,
    description: "Get a GitHub repository. Typed wrapper for the GitHub API.",
    hasSideEffects: true,
    returnType: "object",
    params: [
      { name: "owner", type: "string", description: "Repository owner" },
      { name: "repo", type: "string", description: "Repository name" }
    ],
    example: '["integration/github-get-repo", "almadar-io", "almadar"]'
  },
  "integration/github-create-issue": {
    module: "integration",
    category: "std-integration",
    minArity: 3,
    maxArity: 3,
    description: "Create a GitHub issue. Typed wrapper for the GitHub API.",
    hasSideEffects: true,
    returnType: "object",
    params: [
      { name: "repo", type: "string", description: "Full repo name (owner/repo)" },
      { name: "title", type: "string", description: "Issue title" },
      { name: "body", type: "string", description: "Issue body" }
    ],
    example: '["integration/github-create-issue", "almadar-io/almadar", "Bug: validate fails", "Details..."]'
  }
};

// registry.ts
var STD_OPERATORS = {
  ...CORE_OPERATORS,
  ...MATH_OPERATORS,
  ...STR_OPERATORS,
  ...ARRAY_OPERATORS,
  ...OBJECT_OPERATORS,
  ...JSON_OPERATORS,
  ...TIME_OPERATORS,
  ...VALIDATE_OPERATORS,
  ...FORMAT_OPERATORS,
  ...ASYNC_OPERATORS,
  ...NN_OPERATORS,
  ...TENSOR_OPERATORS,
  ...TRAIN_OPERATORS,
  ...GRAPH_OPERATORS,
  ...CONTRACT_OPERATORS,
  ...DATA_OPERATORS,
  ...PROB_OPERATORS,
  ...OS_OPERATORS,
  ...BROWSER_OPERATORS,
  ...COMPOSITION_OPERATORS,
  ...VEC_OPERATORS,
  ...GEO_OPERATORS,
  ...GRID_OPERATORS,
  ...ANIM_OPERATORS,
  ...EASE_OPERATORS,
  ...NOISE_OPERATORS,
  ...PATH_OPERATORS,
  // Substrate modules
  ...LLM_OPERATORS,
  ...WORKSPACE_OPERATORS,
  ...SESSION_OPERATORS,
  ...MEMORY_OPERATORS,
  ...TRACE_OPERATORS,
  ...BEHAVIOR_OPERATORS,
  ...INTEGRATION_OPERATORS
};
var OPERATOR_NAMES = Object.keys(STD_OPERATORS);
var STD_OPERATORS_BY_MODULE = {
  core: CORE_OPERATORS,
  math: MATH_OPERATORS,
  str: STR_OPERATORS,
  array: ARRAY_OPERATORS,
  object: OBJECT_OPERATORS,
  time: TIME_OPERATORS,
  validate: VALIDATE_OPERATORS,
  format: FORMAT_OPERATORS,
  async: ASYNC_OPERATORS,
  nn: NN_OPERATORS,
  tensor: TENSOR_OPERATORS,
  train: TRAIN_OPERATORS,
  graph: GRAPH_OPERATORS,
  contract: CONTRACT_OPERATORS,
  data: DATA_OPERATORS,
  prob: PROB_OPERATORS,
  os: OS_OPERATORS,
  browser: BROWSER_OPERATORS,
  composition: COMPOSITION_OPERATORS,
  vec: VEC_OPERATORS,
  geo: GEO_OPERATORS,
  grid: GRID_OPERATORS,
  anim: ANIM_OPERATORS,
  ease: EASE_OPERATORS,
  noise: NOISE_OPERATORS,
  path: PATH_OPERATORS,
  // Substrate modules
  llm: LLM_OPERATORS,
  workspace: WORKSPACE_OPERATORS,
  session: SESSION_OPERATORS,
  memory: MEMORY_OPERATORS,
  trace: TRACE_OPERATORS,
  behavior: BEHAVIOR_OPERATORS,
  integration: INTEGRATION_OPERATORS
};
function getStdOperatorMeta(operator) {
  return STD_OPERATORS[operator];
}
function isKnownStdOperator(operator) {
  return operator in STD_OPERATORS;
}
function getModuleOperators(module) {
  return STD_OPERATORS_BY_MODULE[module] || {};
}
function getAllStdOperators() {
  return Object.keys(STD_OPERATORS);
}
function getStdOperatorsByModule(module) {
  return Object.keys(STD_OPERATORS_BY_MODULE[module] || {});
}
function getLambdaOperators() {
  return Object.entries(STD_OPERATORS).filter(([, meta]) => meta.acceptsLambda).map(([name]) => name);
}
function getStdEffectOperators() {
  return Object.entries(STD_OPERATORS).filter(([, meta]) => meta.hasSideEffects).map(([name]) => name);
}
function getStdPureOperators() {
  return Object.entries(STD_OPERATORS).filter(([, meta]) => !meta.hasSideEffects).map(([name]) => name);
}
function validateStdOperatorArity(operator, argCount) {
  const meta = STD_OPERATORS[operator];
  if (!meta) {
    return `Unknown std operator: ${operator}`;
  }
  if (argCount < meta.minArity) {
    return `Operator '${operator}' requires at least ${meta.minArity} argument(s), got ${argCount}`;
  }
  if (meta.maxArity !== null && argCount > meta.maxArity) {
    return `Operator '${operator}' accepts at most ${meta.maxArity} argument(s), got ${argCount}`;
  }
  return null;
}
function assertOperatorArity(operator, argCount) {
  const meta = STD_OPERATORS[operator];
  if (!meta) return;
  const { minArity, maxArity } = meta;
  if (argCount < minArity || maxArity !== null && argCount > maxArity) {
    const range = maxArity === null ? `at least ${minArity}` : minArity === maxArity ? `${minArity}` : `${minArity}..${maxArity}`;
    throw new Error(
      `(${operator} \u2026): expected ${range} argument(s), got ${argCount} \u2014 same bounds the compiled path enforces (canonical-operators.json)`
    );
  }
}
function isStdGuardOperator(operator) {
  const meta = STD_OPERATORS[operator];
  return meta !== void 0 && !meta.hasSideEffects;
}
function isStdEffectOperator(operator) {
  const meta = STD_OPERATORS[operator];
  return meta?.hasSideEffects ?? false;
}
function getOperatorMetaExtended(operator, coreOperators) {
  if (isStdOperator(operator)) {
    return STD_OPERATORS[operator];
  }
  return coreOperators[operator];
}
function isKnownOperatorExtended(operator, coreOperators) {
  if (isStdOperator(operator)) {
    return operator in STD_OPERATORS;
  }
  return operator in coreOperators;
}
function validateOperatorArityExtended(operator, argCount, coreOperators) {
  if (isStdOperator(operator)) {
    return validateStdOperatorArity(operator, argCount);
  }
  const meta = coreOperators[operator];
  if (!meta) {
    return `Unknown operator: ${operator}`;
  }
  if (argCount < meta.minArity) {
    return `Operator '${operator}' requires at least ${meta.minArity} argument(s), got ${argCount}`;
  }
  if (meta.maxArity !== null && argCount > meta.maxArity) {
    return `Operator '${operator}' accepts at most ${meta.maxArity} argument(s), got ${argCount}`;
  }
  return null;
}
function isEffectOperatorExtended(operator, coreOperators) {
  if (isStdOperator(operator)) {
    return isStdEffectOperator(operator);
  }
  const meta = coreOperators[operator];
  return meta?.hasSideEffects ?? false;
}
function getStdLibStats() {
  const byModule = {};
  for (const module of STD_MODULES) {
    byModule[module] = Object.keys(STD_OPERATORS_BY_MODULE[module] || {}).length;
  }
  return {
    totalOperators: Object.keys(STD_OPERATORS).length,
    byModule,
    pureOperators: getStdPureOperators().length,
    effectOperators: getStdEffectOperators().length,
    lambdaOperators: getLambdaOperators().length
  };
}

export { OPERATOR_NAMES, STD_OPERATORS, STD_OPERATORS_BY_MODULE, assertOperatorArity, getAllStdOperators, getLambdaOperators, getModuleOperators, getOperatorMetaExtended, getStdEffectOperators, getStdLibStats, getStdOperatorMeta, getStdOperatorsByModule, getStdPureOperators, isEffectOperatorExtended, isKnownOperatorExtended, isKnownStdOperator, isStdEffectOperator, isStdGuardOperator, validateOperatorArityExtended, validateStdOperatorArity };
